/**
 * Quality tiers + live graphics settings (Graphics G4 + the Esc options menu).
 *
 * Five PRESETS (potato / low / med / high / ultra) each map to a `TierPreset`: a
 * `PostProcessingConfig` (AA + bloom/SSAO/DoF), a pixel-ratio cap, and the
 * LOD / shadow / cull / particle / fog / memory / streaming budgets the other
 * modules read via `activePreset()`.
 *
 * The Esc graphics menu lets the player pick a preset OR tweak an individual
 * feature. To support that, the ACTIVE settings are a live, mutable copy of the
 * chosen preset (`settings_`); a per-feature setter mutates it and flips the
 * state to "custom". `activePreset()` returns that live object, so every consumer
 * (PostModule / Lighting / Culling / Streaming / Weather / Atmosphere / lod) gets
 * the customised values the moment they re-read it (PostModule emits
 * `atlas:quality-changed` after any change, which the others listen to).
 *
 * Settings persist to localStorage and restore on boot; first run auto-detects a
 * starting preset from the device so a weak machine isn't dropped into Ultra.
 *
 * AA lives in the POST chain (FXAA/SMAA), MSAA OFF (`App.create antialias:false`) —
 * sidesteps the r182 "GTAO + WebGPU + MSAA → invalid WGSL" gotcha so SSAO can run.
 */
import type { PostProcessingConfig } from 'enginex/render/PostProcessingPipeline.js';

export type UpscaleMode = 'off' | 'ultra' | 'quality' | 'balanced' | 'performance';
export const UPSCALE_RATIO: Record<UpscaleMode, number> = { off: 1, ultra: 1.3, quality: 1.5, balanced: 1.7, performance: 2 };
export type Tier = 'minimal' | 'potato' | 'low' | 'med' | 'high' | 'ultra';

export interface TierPreset {
  post: PostProcessingConfig;
  pixelRatio: number;   // cap, clamped against devicePixelRatio at apply (the render-scale lever) — used when targetHeight is 0
  /**
   * The CANVAS height each tier targets, in pixels (2026-09-08, "potato and
   * minimal very low, ultra 4K"): the pixel ratio becomes targetHeight ÷ the
   * window's CSS height (clamped MIN..MAX_RENDER_SCALE), whatever the display —
   * ultra is a 3840×2160 canvas on a 1080p window (supersampled) and native on
   * a 4K one; minimal is 480p. 0 = native (the pixelRatio cap clamped to the
   * display). The FSR ratio then divides the SCENE below the canvas again.
   */
  targetHeight: number;
  /**
   * FSR 1 UPSCALING (perf frontier 2026-09-08, item 1): the scene pass renders
   * at 1/ratio of the drawing buffer and the engine's EASU + RCAS kernels
   * reconstruct the output size before any other pass. 'ultra' 1.3× /
   * 'quality' 1.5× / 'balanced' 1.7× / 'performance' 2×, 'off' = native.
   * Shading cost falls with the square of the ratio; the picture stays close
   * to native (the interim for temporal upscaling). Never applied in XR.
   */
  upscale: UpscaleMode;
  /** 'fsr' = spatial EASU/RCAS (default); 'taau' = the temporal resolve over the low-res pass (TRAA history + RCAS). */
  upscaleMode: 'fsr' | 'taau';
  /** Cloud raymarch resolution as a fraction of the drawing buffer (0.5 = a quarter of the rays); 1 = full. */
  cloudResolution: number;
  /**
   * Adaptive render scale: a frame-time governor moves the FSR ratio live
   * between the tier's own ratio and 2× when the machine cannot hold its
   * refresh rate, and probes back up when it can (lib/frame_governor.ts).
   */
  adaptive: boolean;
  particleCap: number;  // 0..1 scale on weather particle density (G4)
  fogScale: number;     // multiplier on atmosphere fog density (G4)
  cloudsEnabled: boolean; // volumetric clouds composite on/off (PostModule reads this)
  shadowsEnabled: boolean; // false = no CSM at all (potato) — the #2 GPU lever after render scale
  /**
   * Flat multiplier on sun + hemisphere-ambient intensity (LightingModule).
   * 1 everywhere except potato: with bloom, clouds, fog haze and surface detail
   * all stripped, the image loses the luminance those features carry and reads
   * uniformly dark — so potato compensates in the LIGHTS (free) instead of
   * re-enabling effects (which would defeat the tier). NOT a menu feature.
   */
  lightingBoost: number;
  /**
   * Multiplier on the settlement streaming radii (city 1000 / town 650 /
   * hamlet 300 m). Settlement content is INSTANCED and never per-object
   * culled, so `farCull` does not touch it — this is the only lever that
   * shrinks how much settlement draw-set is resident. Draw-ITEM count is the
   * render-thread bottleneck (three.js #30560), which pixel-side knobs
   * (render scale, shadows, post) cannot reduce.
   */
  settlementRadiusScale: number;
  /**
   * Always assemble settlements at the simplified LOD-1 variants, and never
   * swap back to LOD-0 up close. Kills BOTH costs of the two-level civ LOD:
   * the LOD-0 triangle load and the dispose→rebuild churn at the 380/460 m
   * hysteresis band (a whole-settlement teardown + 30-50 GLB reload — the
   * "sometimes lag while walking" hitch).
   */
  settlementForceLowLod: boolean;
  /**
   * Distant detail (2026-09-08, "show nothing past the drawing distance"):
   * true = the representations BEYOND the draw distance — settlement HLOD
   * prefabs + silhouette boxes, the terrain far tier and its far sea; false
   * = nothing past the draw distance (the fog closes there), and a
   * settlement builds only inside it. The in-range LOD ladder is untouched
   * either way — on a weak GPU it is what keeps a near city drawable.
   */
  distantDetail: boolean;
  /**
   * Admin-placement LOD (2026-09-08, the minimal tier): the cooked variant
   * every placed object renders at in play mode (0 = full … 3 = 5 %); props
   * fall back to their nearest lower level. Placements are ALSO distance-
   * culled by `farCull` (lib/placement_cull.ts).
   */
  placementLod: 0 | 1 | 2 | 3;
  /** Civ structures a settlement ASSEMBLES at: 1 (57 % of LOD0) or 2 (16 %); 2 also caps the in-range ladder at LOD2. */
  settlementBuildLod: 1 | 2;
  /** River ribbons: the engine water (the sea's material) or the cheap scrolled normal map. */
  riverWater: 'realistic' | 'simple';
  /** Terrain ground: the 37-sample climate/band stack, or ONE albedo sample tinted by the biome weights (applies at the next world entry). */
  groundQuality: 'full' | 'simple';
  /** Sky: the Hillaire atmosphere + IBL bake + night photo, or a flat sky the colour of the fog (applies at the next world entry). */
  skyQuality: 'full' | 'flat';
  /**
   * Colour LOOK (shader wave 2026-09-08): a procedural 3D LUT (lib/lut_looks)
   * handed to the engine's `lut3D` pass — 'none' removes the pass.
   */
  look: 'none' | 'natural' | 'cinematic' | 'warm' | 'cool' | 'noir' | 'cosmos' | 'vivid';
  /** Foliage translucency (leaves lit from behind) + hierarchical wind on the tree species (shader wave stage 2; applies as chunks stream). */
  foliageShading: boolean;
  /** Realistic fluid physics (2026-09-11): the camera-following interaction
   *  field (wakes, wading rings, splashes, rain on water), wind-driven sea
   *  state, swimming + wave-following rides. Off = today's water exactly. */
  fluidPhysics: boolean;
  /** Material effects on props/structures: snow accumulation by world-up + clearcoat on alloys, iridescence on bio-glass (applies to newly loaded assets). */
  materialFx: boolean;
  // ── Visual-realism wave, materials / AA / camera / characters (2026-09-25; merged per tier by
  //    REALISM_MATERIAL_TIERS below — optional so the preset rows stay untouched) ──
  /** R6: world-space macro grime in crevices / occlusion / ledges (≤ 12 % darker, ≤ 0.15 rougher), with the finish's snow-in-crevices; parse-time graph, LIVE strength. */
  materialGrime?: boolean;
  /** R3.4: textures sample with a mip bias that follows the render scale (log2(1/scale), −0.5 cap on spatial AA, −0.5 more under TAAU). LIVE. */
  mipBias?: boolean;
  /** R6: the procedural detail surface on props and decor too (unset = follows surfaceDetail). Parse-time. */
  propSurfaceDetail?: boolean;
  /** R4: depth of field only in THIRD person, autofocused on the player through a physical lens ('off' = never). First person is always sharp. */
  cameraDof?: 'off' | 'thirdPerson';
  /** R4: the camera model's f-number (5.6 everyday; the dialogue / traveler lens opens to f/2). */
  cameraFNumber?: number;
  /** R8: the player's own third-person body and every remote traveler as a RIGGED actor (the donor skeleton + the
   *  shared clips, `avatar-rig:player_*`) instead of the static scan. Applies as bodies are (re)built. */
  playerBodyRig?: boolean;
  /** R8: two-bone foot IK on near actors (< 15 m) — feet on slopes and steps, pelvis lowered. */
  footIk?: boolean;
  /** R8: procedural breathing on idle / talk / listen actors. */
  breathing?: boolean;
  /** Interior mapping on window/glass materials (rooms behind the panes, lit by night) — shader wave stage 3; applies to newly loaded structures. */
  interiorMapping: boolean;
  /** VFX pack: soft particles (mist, tracers), dissolve on a creature's death, shield shell on a hull hit, heat haze behind a hovercraft, holograms over notice boards. */
  vfxShaders: boolean;
  /** Impact decals lit with baked crater normals (else unlit flat marks). */
  litDecals: boolean;
  /** Street lamps as spotlights projecting a fixture cookie (applies at the next world entry). */
  lampCookies: boolean;
  /** Persistent whitecap trails on the sea (a CPU foam grid the water samples; next world entry). */
  waterFoam: boolean;
  /** Projected caustics on the sea floor (an extra small render per frame; next world entry). */
  waterCaustics: boolean;
  /** Cloud shadows on the ground, foliage and structures from the cloud field's own weather map (shader wave stage 4). */
  cloudShadows: boolean;
  /** Stochastic (Deliot–Heitz) sampling of the ground albedos — no visible tile grid (applies at the next world entry). */
  groundAntiTiling: boolean;
  // ── Ground material v2 (2026-09-25, research §7 R1 / R7 / R8; lib/ground_layers.ts). Optional: read through groundKnobs(). ──
  /** 'v2' = twelve scanned CC0 layers in two KTX2 arrays, placed per vertex by rules (slope, curvature, wetness, water, altitude, forest, roads); 'v1' = the six biome albedos (kept for A/B). Rebuilds the ground live. */
  groundMaterial?: 'v1' | 'v2';
  /** v2 array resolution (512 / 1024 / 2048 — ~8 / ~34 / ~134 MB of GPU memory with mips). */
  // ground (2026-09-26): the owner's "go up to 8k" — 4096 / 8192 (~537 MB / ~2.1 GB BC7/ASTC), cooked from the 8K scans;
  // a rung the device cannot hold falls down THE RUNG LADDER (lib/ground_textures.ts).
  groundArrayRes?: 512 | 1024 | 2048 | 4096 | 8192;
  /** v2: the potato tier's 8-layer array (strata → rock, dune → beach, litter + alien → loam). */
  groundPotatoSet?: boolean;
  /** v2: rock and strata projected along X/Z on steep faces (+4 taps where it is steep). */
  groundTriplanar?: boolean;
  /** v2 tile bands: 3 = 3.5 → 14 → 56 m cross-faded; 2 = 14 → 56 m (one cross-fade fewer). */
  groundBands?: 2 | 3;
  /** R8c: parallax occlusion under 25 m — 'rock' on rock / strata / gravel, 'all' on every near layer. */
  groundPom?: 'off' | 'rock' | 'all';
  /** R8a: the ring-0 terrain chunks' seg (48 = 5.3 m cells; 96 = 2.7 m, +~120k triangles, a flag). */
  groundNearSeg?: 48 | 96;
  // surfaces (2026-09-27): §2.1 micro-shadowing (default on), §2.3 hex-tiling on the dominant layer (high / ultra; med keeps
  // the IQ 2-fetch), §2.4 the parallax self-shadow march (a flag, default off — lib/wetness.ts applyGroundV2).
  groundMicroShadow?: boolean;
  groundHexTiling?: boolean;
  groundPomShadow?: boolean;
  /** Raindrop ripple rings on up-facing ground while it rains (applies at the next world entry). */
  rainRipples: boolean;
  /** Aurora curtains on some Commons nights. */
  aurora: boolean;
  /** Raymarched nebula volumes at the space nebula sites (next sector entry). */
  nebulae: boolean;
  // ── The space realism wave (2026-09-25, report §9 — the space agent; set per tier in SPACE_TIER_FLAGS below) ──
  /** The sector's real sky: the Milky Way photo + the 114 k-star catalogue, rotated per system (next sector entry; off = the legacy void). */
  spaceSky?: boolean;
  /** Speed cues in space: velocity dust, star streaks at overcruise / hyper, the FOV kick, the hit shake (live). */
  spaceMotion?: boolean;
  /** Engine plumes on every hull, sized from its stern (live). */
  spacePlumes?: boolean;
  /** War hulls and stations LIT under the sector sun + the window layer and nav lights (next sector entry; off = the unlit bake). */
  spaceLitHulls?: boolean;
  /** The player's shield ripples, collapse crackle, hull scorches and venting (live). */
  spaceShields?: boolean;
  /** shader gaps R4 — receiver-side eclipses: a hull or a planet in another body's shadow goes dark, with a limb-darkened penumbra (in space; next entry builds, live off). */
  eclipseShadows?: boolean;
  /** shader gaps R4 — planetary rings on Vorune and the derived gas giants, with ring ↔ planet shadows (in space; next entry builds, live off). */
  planetRings?: boolean;
  /** shader gaps R4 — the Commons' cloud shell from orbit, baked from the ground's own weather field (in space; next entry). */
  orbitalClouds?: boolean;
  /**
   * shader gaps: black hole — a placed black hole's lens (engine render/space/blackhole, Bruneton 2020): 'off' draws
   * none, 'low' 257×256 / 64×32 tables and one star sample per pixel, 'high' 513×512 / 128×64 and a 2×2 star
   * supersample in the hole's core. Live (a placed hole rebuilds at the new size; off hides it). No destination
   * exists yet — `__blackHole()` places one.
   */
  blackHole?: 'off' | 'low' | 'high';
  /** O'Neil atmosphere shells on the planets seen from orbit (next sector entry). */
  planetAtmosphere: boolean;
  /** Light shafts around the camera while submerged (next world entry). */
  underwaterShafts: boolean;
  /** Dual-quaternion skinning on the near versona actors (new actors). */
  dqSkinning: boolean;
  // ── Shader additions wave (2026-09-15) ──
  /** Toksvig normal-map roughness filtering — specular sparkle at the source (materials built after the flag). */
  specularAA: boolean;
  /** Footprint-filtered procedural detail: no shimmer at distance, unresolved bumps become roughness (next parse). */
  proceduralFiltering: boolean;
  /** The shadowed light VOLUME around the player — sun shafts, lamp cones (live). */
  volumetricLights: boolean;
  /** Six-way lit smoke on impacts and blown dust (new emitters). */
  litSmoke: boolean;
  /** LTC rect area lights on lit windows at night (next world entry — the light-set identity law). */
  areaLights: boolean;
  /** Real transmission on prop / decor glass and canopies (next parse). */
  glassTransmission: boolean;
  /** Brushed-metal anisotropy on metallic props and weapons (next parse). */
  hardSurfaceAnisotropy: boolean;
  /** Light-return lobe on signs and markers (next parse). */
  retroreflection: boolean;
  // ── Render wave 1 (2026-09-17) ──
  /** Blend the three nearest impostor frames instead of snapping to one (far trees; next bake). */
  impostorBlend: boolean;
  /** A top-down roof map around the player: rain and snow stop under cover and the ground under it stays dry (live). */
  rainShelter: boolean;
  /** Exposure metering: the log average, or the percentile-trimmed histogram. */
  autoExposureMetering: 'average' | 'histogram';
  // ── Fire realism (2026-09-25, lib/vfx/fire_rules.ts) ──
  /** The fire shader rung: blackbody SPRITES, cylindrical CARDS, the procedural VOLUME march (both backends), or the shared compute SIM (WebGPU; the nearest two fires). Live. */
  fireQuality: 'sprite' | 'cards' | 'volume' | 'sim';
  /** Pool lights the fires may hold (1 / 2 / 3) — the light broker's size is fixed for the session (the light-set identity law): next world entry. */
  fireLights: number;
  /** Heat haze over the flames (a scene-colour refraction quad; WebGL 2, or WebGPU with 'atlas:haze-webgpu'). Live. */
  fireHaze: boolean;
  // ── Vegetation realism (2026-09-25, visual-realism R2–R12: lib/grass, lib/geology, lib/ecology) ──
  /** The vertex-procedural grass layer: 'cards' (clump cards only), 'low' (16 k blades to 12.5 m), 'med', 'high' (~59 k to 24.5 m + cards to 57 m), 'ultra'. Live. */
  grassLayer: 'off' | 'cards' | 'low' | 'med' | 'high' | 'ultra';
  /** Geology density (cliff faces, outcrops, talus, embedded boulders) 0..1.25; potato keeps cliffs + large boulders only. Re-scatters live. */
  geologyDensity: number;
  /** Understory and clutter density (shrubs, ferns, flowers, logs, stumps, mushrooms, pebbles, riparian reeds) 0..1.25. Re-scatters live. */
  understoryDensity: number;
  /** Forest STAND impostors from the foliage ring's edge out to this many metres (0 = off). Live. */
  forestHorizonM: number;
  /** The far terrain carries the woods: canopy tint + normal, and on its coarse levels a canopy lift (next far-tier rebuild). */
  farCanopy: boolean;
  /** Per-instance hue / value / roughness jitter on foliage and props (next material build). */
  foliageJitter: boolean;
  /**
   * Ambient life: falling leaves in the woods, pollen motes over meadows by day, dust devils in the windy desert
   * (AtlasAmbientLifeModule, lib/ambient_life.ts) and the woods' insect swarm (AtlasFaunaModule) — all sized by
   * `particleCap` (0 = none). Live. Readers since the gaps wave round 2 (2026-09-28); the flag stood unread before.
   * The ambient life wave (2026-09-29) added grazing herds on the plains, wetland and highlands (AtlasHerdsModule,
   * lib/ambient_herds.ts) and butterflies, dragonflies and beach crabs (AtlasCrittersModule, lib/ambient_critters.ts).
   */
  ambientLife: boolean;
  /**
   * CAVE LIGHT (shader gaps program, 2026-09-29; `lib/cave_light*.ts`, AtlasCaveModule): each built cave bakes a probe
   * grid in its worker — the SKY TRANSFER (how much of the live sky reaches each point, one bounce included) and the
   * crystals' LOCAL irradiance — and its rock / crystals take the sky's hemisphere + IBL only through that transfer;
   * the global ambient takes one eased multiplier + tint while the player is inside (LightingModule's hook).
   * 'visibility' = the sky transfer alone (cheap and correct — every tier that builds caves); 'baked' + the crystals'
   * light on the rock; 'baked+probe' + a local reflection probe (a CubeCamera capture per cave, PMREM, box-projected)
   * for the crystals. Live. `localStorage 'atlas:cave-light' = 'off'` is the kill switch.
   */
  caveLight?: 'off' | 'visibility' | 'baked' | 'baked+probe';
  /**
   * PERSISTENT MATERIAL DAMAGE (shader gaps program round 2, 2026-09-29; `lib/damage_*.ts`): a hit changes the
   * SURFACE it lands on — coated metal strips to bare iron ringed by heat tint and burnt paint, rock chips lighter,
   * wood chars, soil gouges, glass crazes — composed onto the receiver's own material BEFORE lighting and kept for the
   * session (client memory, capped by bytes, cleared on a world change). 'marks' = albedo / roughness / metalness;
   * 'full' + the crater normal and the heat glow. Parse-time for asset materials, entry-time for the ground; the draw
   * is live (off = nothing uploaded). `localStorage 'atlas:damage' = 'off'` is the kill switch.
   */
  materialDamage?: 'off' | 'marks' | 'full';
  /**
   * MATERIAL MODELS (shader gaps program round 4, 2026-09-30; `lib/material_models.ts`, engine `render/materials/`):
   * `eonDiffuse` — the energy-preserving Oren–Nayar rough diffuse (Portsmouth et al. 2025) on the rough rock families
   * (their sunlit faces flatten and back-scatter like real stone; albedos kept exactly). `laplacianBlend` — Wronski's
   * Laplacian blend on the ground's gravel ↔ rock transition, its Gaussian level only (the mean colour glides over the
   * rules' ramp, the detail keeps the height blend; two taps in the band) — OFF in every preset (an opt-in): measured on
   * the bed it bought no seam (gravel ↔ rock 0.0893 → 0.0896) and cost band contrast (0.712 → 0.696 against the pure
   * layers' 0.720) — the height blend already keeps the edge crisp (impl-material-models.md). `flakeGlints` — footprint-filtered mica flakes on the slate (Deliot & Belcour 2023), lit by the sun,
   * replacing its old emissive glint. Built into materials (the rocks at first use, the ground at its next build) —
   * reload after a change. Kill switches `atlas:eon` / `atlas:laplacian` / `atlas:flakes` = 'off'.
   */
  eonDiffuse?: boolean;
  laplacianBlend?: boolean;
  flakeGlints?: boolean;
  /**
   * WINDOW-LIGHT SHADOWS (shader gaps R5, agent J, 2026-09-30; `lib/pane_visibility.ts`, `lib/pane_light_node.ts`): the
   * pooled window rect lights (`areaLights`) are shadowed by the PEOPLE near their pane — the player, other travelers,
   * walking townsfolk as vertical capsules — through four stratified samples of the rectangle weighted by the
   * unshadowed integrand (Heitz, Hill & McGuire 2018's ratio estimator; never one centre ray). Only while someone
   * stands within 14 m of a seated pane. Built into the pooled lights at init — reload after a change.
   * `localStorage 'atlas:pane-shadows' = 'off'` is the kill switch. The facing and room rules are not a flag.
   */
  windowShadows?: boolean;
  /**
   * SKIN SHADING (shader gaps program round 2, 2026-09-29; `lib/skin_*.ts`): the near rigged actors (the town layer's
   * ≤ 24, the sky-ship crew, player bodies) wear a skin response on their rig material — a per-fragment skin mask from
   * the albedo's chroma, the body's luminance floor and its bone regions; Penner's pre-integrated diffuse by curvature
   * (the red terminator) with per-channel bent normals; a red back-light transmission through thin regions. Specular,
   * clothing, hair and metal unchanged; the crowd twins keep their material. High / ultra (the tiers whose rigs are the
   * full 290k mesh up close). Live. `localStorage 'atlas:skin' = 'off'` is the kill switch.
   */
  skinShading?: boolean;
  /**
   * WEATHER ON SURFACES (shader gaps program round 3, 2026-09-30; `lib/wet_state.ts`, `lib/wet_layer.ts`): THE ONE WET
   * CLOCK — rain wets by its rate × the roof map's exposure, evaporation dries by temperature, humidity, wind and sun
   * over MINUTES (the film first, the pores last), per surface class. 'ground' = the ground reads the clock (absorbed
   * darkening, film gloss, ponded puddles); 'all' also wets props and structures (Lagarde's porous darkening + surface
   * film, composed into the finish graph at parse); 'off' = the old 2 s wetness lerp. `atlas:wet = 'off'` kills it.
   */
  wetSurfaces?: 'off' | 'ground' | 'all';
  /**
   * SNOW AND MUD THAT REMEMBER (shader gaps program round 3, 2026-09-30; `lib/deform_field.ts`, `lib/deform_layer.ts`):
   * the engine's deformation heightmap as a camera-following 38 m window, pressed by every stride (the player, remotes,
   * walkers) and a hovercraft's cushion over snow, filled by fresh snowfall, read by the v2 ground as an imprint (the
   * dent's slope bends the normal; packed snow greyer and smoother, a rut darker and wet) through the roof map's atlas —
   * no sampler. Needs the roof map. Applies at the ground's next build. `atlas:deform = 'off'` kills it.
   */
  surfaceDeform?: boolean;
  /**
   * LOCAL VOLUMES (shader gaps program round 3, agent F, 2026-09-30; engine ShadowedLightVolume v3, lib/volume_medium.ts):
   * where the shadowed light volume (`volumetricLights`) marches. 'half' = a quarter of the pixels (half width × half
   * height of the scene pass), rebuilt at full resolution by a depth-aware composite that keeps silhouettes (an edge
   * pixel with no matching coarse sample is marched at full resolution); 'full' = every pixel. Both composite
   * correctly (the in-scatter at full strength — v2 dimmed it by (1 − T)) and split the march around the nearest fires
   * so a fire inside the haze is dimmed by the haze in front of it. Live (the volume is rebuilt).
   * `localStorage 'atlas:volumes' = 'legacy'` restores the v2 volume exactly.
   */
  volumeResolution?: 'full' | 'half';
  /**
   * The light volume's per-pixel march jitter: 'golden' (the engine's 2D blue-noise mask advanced by the golden ratio —
   * the default), 'stbn' (spatiotemporal blue noise, Wolfe et al. EGSR 2022 — generated in the repo; its generation
   * method is PATENTED by NVIDIA (US 12,283,028 B2), so it is opt-in pending the owner's decision), 'white' (v2's
   * hash). Live (a uniform).
   */
  volumeNoise?: 'golden' | 'stbn' | 'white';
  /**
   * THE FFT OCEAN (shader gaps program round 5, 2026-09-30; engine `WaveProvider` + `fft/FftWaveProvider`): the Commons
   * sea's waves from one Tessendorf patch (256² over 512 m, TSL compute) instead of the eight-wave Gerstner set — a
   * broad wind-driven spectrum (JONSWAP; the sea state's wind shapes it, cross-faded over 20 s; its energy is the
   * Gerstner sea's), displaced only where the 8 m grid can carry it, the shorter band folded into roughness by distance,
   * whitecaps from its own Jacobian. Riders, swimmers, fish and the waterline stand on the SAME drawn surface (a CPU
   * mirror of the band, latency 0). WebGPU only — on WebGL2 (and in XR) the Gerstner sea draws. Ultra. The sea rebuilds
   * on a change. `localStorage 'atlas:fft-ocean' = 'off'` kills it.
   */
  fftOcean?: boolean;
  /**
   * RAIN PUDDLES THAT RUN (shader gaps program round 6, 2026-09-30; engine `ShallowWaterGpu`, `lib/puddle_sim.ts`): a
   * camera-following shallow-water patch (virtual pipes, TSL compute) on the drawn ground — rain × the roof map's exposure
   * runs downhill and pools in the terrain's real lows and in the static hollows (their noise is micro-relief in its bed),
   * soaks in by ground class, dries at the wet clock's rates, drains into still water. Inside the patch the ground's
   * puddles read it; its last 8 m hand back to the static puddles, which stay everywhere else. 'small' 64 m · 'medium'
   * 96 m · 'large' 128 m at 0.5 m cells. WebGPU only (WebGL2 and XR keep the static puddles). Needs the wet clock.
   * Applies at the ground's next build. `localStorage 'atlas:puddles' = 'off'` kills it. Never gameplay.
   */
  puddleSim?: 'off' | 'small' | 'medium' | 'large';
  /**
   * THE INTERIOR QUALITY OVERLAY (interiors programme, slice D, 2026-10-01; the one table is `lib/interior_style.ts
   * INTERIOR_DETAIL_RULES`): 0 essential · 1 standard · 2 full. It picks the interior's texture set (128 / 256 / 512 px
   * shared canvases; wear and scuffs from 1), whether lit indicators on furniture (lamp shades, screens) glow (from 1),
   * the room's sound (0 = the one generic room and no bed; from 1 the room's own acoustic preset and its quiet
   * non-vocal bed) and whether DETAIL-tier decor is placed (2 — read by the furnishing planner). It NEVER moves a wall,
   * a stair, a service anchor, a seat or an owned piece and never changes the floor count: every tier walks the same
   * room. Applies to the next room entered. `localStorage 'atlas:interior-look' = 'off'` is the look's own lever.
   */
  interiorDetail?: 0 | 1 | 2;
  meshSmoothing: boolean; // loader-time creased normals on structure GLBs (one-time cost, ~0 runtime)
  surfaceDetail: boolean; // TSL procedural detail-normal + roughness breakup (per-fragment ALU)
  /**
   * 0..1 scale on chunk-foliage scatter density. Foliage is the TRIANGLE WHALE
   * of the whole frame — the per-tier census showed one shared-instancer group
   * of ~4,270 trees carrying 64.5M of coast-surf's 64.7M triangles at EVERY
   * tier (≈15k tri per tree, no LOD, per-chunk culling traded away in the
   * instancer consolidation). This is the one knob that shrinks it. Consumed
   * live by EnvironmentModule.scatterChunkFoliage; a tier switch re-scatters.
   */
  foliageDensityScale: number;
  // ── G5: scale the rest of the pipeline (LOD / shadows / culling / texture / memory / streaming) ──
  lodDistanceScale: number; // G2 lib/lod buildLOD distance multiplier (lower = swap to low-LOD sooner)
  shadowMapSize: number;    // G3 CSM per-cascade shadow map size
  shadowCascades: number;   // G3 CSM cascade count
  shadowMaxFar: number;     // G3 CSM far distance
  /** sky/post (2026-09-27): THE MOON's own shadow map (one DirectionalLight map around the camera) — high / ultra only. */
  moonShadows?: boolean;
  farCull: number;          // G2 RenderCulling distance cutoff (m) — the max object draw distance
  /** Commons terrain streaming window RADIUS in 256 m chunks (draw distance for the ground). */
  chunkRadius: number;
  /** Foliage scatters only within this Chebyshev ring — kept small so extending
   *  chunkRadius grows the terrain footprint WITHOUT multiplying the tree whale. */
  foliageRing: number;
  maxTextureRes: number;    // ENFORCED: AssetModule caps compressed mip chains to this at parse (texture_budget.ts)
  /**
   * Settlement study R2: the LOD the NEAR foliage ring and settlement props
   * load. 1 = the cooked LOD1 variant (half the triangles, 512² textures —
   * a shrub is 0.8 MB instead of 10 MB); 0 = the canonical LOD0. The far
   * foliage ring is always LOD1. High/ultra keep LOD0 up close.
   */
  /**
   * Cap on the near-skinned versona ACTORS (`lib/versona_rig.ts`): wave-2
   * bodies are ~290k-triangle SkinnedMeshes, so 24 of them is a desktop-GPU
   * budget; lower tiers draw fewer as actors and the rest as instanced twins.
   */
  nearActors: number;
  /**
   * LOD the near foliage ring loads: 0 canonical, 1 half, 2 fifteen percent.
   * Foliage is the frame's triangle whale (a census found 78 % of a live
   * frame's triangles in 151 foliage instances), so the cheap tiers take 2.
   */
  nearFoliageLod: 0 | 1 | 2;
  /** Props and decor inside settlements: 1 = LOD1 variant, 0 = canonical. */
  /** Prop/decor LOD in settlements: 0 full, 1 = 50 %, 2 = 15 % of LOD0 (real since the 2026-09-18 hinterland re-cook). */
  propLod: 0 | 1 | 2;
  /** The far versona body (instanced twin): 0 = the full 290k mesh ("we want 300k vertices" — high/ultra),
   *  1 = the cooker's LOD1 (~145k), 2 = LOD2 (~45k, seam-respecting, compare-verified). A software or
   *  weak GPU cannot parse and draw eighteen 290k bodies; the near actors are unaffected. */
  farBodyLod: 0 | 1 | 2;
  drawCallBudget: number;   // G2 draw-call ceiling (HUD warn threshold)
  memoryBudgetMb: number;   // G5 VRAM budget (LRU evicts above it)
  prefetchRegions: number;  // G5 regions to prefetch ahead along the movement vector
  /**
   * Hillaire aerial-perspective composite (distance haze on geometry). The
   * engine bakes the froxel volume only when the sky module was CREATED with
   * it on — this flag gates that at boot AND the PostModule composite pass,
   * so a runtime change takes effect after reload (the menu says so).
   */
  aerialEnabled: boolean;
  /** Auto-exposure eye adaptation (engine AutoExposureSystem). Opt-in. */
  autoExposure: boolean;
  /**
   * Second LOW scattered cloud layer (~800-1500 m). The high deck sits at
   * 3800-9600 m and barely parallaxes at walk speed; the low puffs swing ~4°
   * per 50 m walked, making the sky's world-anchoring legible. Costs a
   * second (cheap-preset) raymarch, so med keeps it off.
   */
  lowClouds: boolean;
  /**
   * Cloud camera-translation response (engine parallaxScale, live uniform).
   * 0 = frozen to the camera (HDRP "Rendering Space: Camera"); 1 = full world
   * parallax — clouds are STATIONARY in the world (the 2026-08-19 shipped
   * default, per user directive: the 0.15 "travel evolution" compromise was
   * perceptually indistinguishable from camera-glued at walking scale and read
   * as "the clouds follow me"). Walk-speed parallax is physically small on the
   * 3.8 km deck (moon-follows-the-car); the low puffs (~4° per 50 m) make the
   * anchoring legible. Taste knob, same on every tier.
   */
  cloudParallax: number;
  /** AA quality 2|4|8|16|32 — iterated edge-resolve passes (engine AA section). */
  aaQuality: number;
  /** Auto-exposure adaptation strength 0..1 (1 = full eye adaptation). */
  autoExposureStrength: number;
  // ── Graphics-menu completion wave (all optional — preset defaults apply) ──
  /** Texture anisotropy 1|2|4|8|16 (AssetModule applies at parse; a quality
   *  change re-streams via the StreamingModule evict). Default 4. */
  anisotropy?: number;
  /**
   * Exposure (2026-09-25, lib/photometry): under the default 'physical' mode the
   * time-of-day CURVE sets the exposure (the lights are pre-exposed by it) and
   * this is a COMPENSATION multiplier around it (0.85 = neutral); under
   * 'fixed' it is the renderer's own fixed exposure, as before. Default 0.85.
   */
  tonemapExposure?: number;
  /** Renderer tone-mapping operator. Default 'agx' (2026-09-25: hue-stable to white); 'aces2' = the engine's hue-preserving ACES 2 node at the display boundary. */
  tonemapOperator?: 'aces' | 'agx' | 'aces2' | 'neutral' | 'reinhard';
  /** 'physical' (default) = the exposure curve by sun elevation + weather (+ the metering when autoExposure); 'fixed' = a fixed camera exposure (nights go dark). */
  exposureMode?: 'physical' | 'fixed';
  /** The metered correction's clamp around the curve, in stops (default 1.5; potato 1 — §10 R2's per-tier chain). */
  autoExposureRangeEv?: number;
  /** The high étage: a plane-projected cirrus sheet at 8.5 km in the sky (unset = with the clouds; next world entry). */
  highClouds?: boolean;
  /** Volumetric-cloud raymarch quality override ('auto' = the tier mapping). */
  cloudMarchQuality?: 'auto' | 'low' | 'medium' | 'high';
  /** Planar water reflections (heavy — AtlasWaterModule rebuilds on change). */
  waterReflections?: boolean;
  // ── Water realism wave (2026-09-25; lib/water_look.ts) — per-tier values in WATER_TIERS below ──
  /** R1: the water reflects the sky PMREM (needs skyQuality 'full'; else the flat tint). */
  waterSkyReflection?: boolean;
  /** R1 follow-up: clouds in the water's reflection (the cloud field's coverage map). */
  waterCloudReflection?: boolean;
  /** R2: the spectrum-baked detail normal maps (else the old four-sine map). */
  waterSpectrumNormals?: boolean;
  /** R3: crest + body scattering (backlit crests glow). */
  waterScattering?: boolean;
  /** R4: distance-aware glitter (footprint-filtered waves, the disc sun). */
  waterGlitterFilter?: boolean;
  /** R5: the far tier's sea wears the same water (else a lit standard material). */
  waterFarSea?: boolean;
  /** R6: the analytic bed under the shallows (the WebGPU shallows). */
  waterAnalyticBed?: boolean;
  /** R7: caustics + per-channel attenuation on the ground under the sea. */
  waterBedCaustics?: boolean;
  /** R12: per-channel underwater absorption + in-scatter (else the monochrome fog). */
  waterUnderwaterMedium?: boolean;
  /** R12: marine snow + bubbles under water. */
  waterMarineLife?: boolean;
  /** R13: calm lakes with wind cat's-paws. */
  waterCalmLakes?: boolean;
  /** R14: waterfall soft particles, the rainbow in the mist, the nappe's strands. */
  waterfallFx?: boolean;
  /** R15: planar reflections render only for the ONE body nearest the camera (others fall back to the PMREM). */
  waterReflectionBudget?: boolean;
  /** R17: splash crowns + floating debris. */
  waterSplashCrowns?: boolean;
  /** R10: the camera-centred GPU foam memory (half-metre texels, 256 m) — the CPU grid stays the far fallback. Needs waterFoam. */
  waterFoamField?: boolean;
  /** R11: river v2 — Manning speeds per vertex, two-phase flow, Froude white water. */
  waterRiverFlow?: boolean;
  /** Colourblind daltonization (engine ColorblindFilterNode, display-referred). */
  colorblindMode?: 'off' | 'protanopia' | 'deuteranopia' | 'tritanopia';
  colorblindStrength?: number;
  /** Camera vertical FOV in degrees; 0 = leave the engine default. */
  cameraFov?: number;
  /**
   * Tree/foliage WIND, on its own switch (2026-09-14). Unset = follow
   * `foliageShading`, which is how every stock preset behaves; setting it
   * lets a custom profile keep the cheap tiers and still have the trees move
   * (or run the leaf lighting with the trees still). Read it through
   * `foliageWindOn()`, never directly.
   */
  foliageWind?: boolean;
  /** Adaptive auto-tune: walk the tier ladder to hold ~50+ fps. */
  autoTune?: boolean;
}

export const TIERS: Record<Tier, TierPreset> = {
  // Potato: every knob floored so a ~3.2 GHz quad-core with a mid-range GPU runs
  // lag-free. Levers ranked by measured impact: render scale (pixelRatio 0.5 =
  // quarter the fragments), shadows fully OFF (no CSM passes at all), no bloom/
  // AO/DoF (the engine pipeline still applies its default FXAA, which smooths the
  // 0.5-scale upsample), shortest draw distance the slider allows, no clouds/
  // particles/surface detail, earliest LOD swap, halved texture + memory budgets.
  // lightingBoost 1.35 compensates the luminance bloom/clouds/haze carry at
  // higher tiers — without it the tier reads uniformly dark.
  // MINIMAL — below potato (2026-09-08, "implement minimal graphics mode …
  // everything on lowest settings"): every lever at its floor, the void past
  // 500 m, placements at 5 % detail and culled by distance, settlements
  // assembled at LOD2, no skinned actors, the cheap river, a one-sample
  // ground and a flat sky. The in-range LOD ladder still runs (capped at 2).
  minimal: {
    post: { fxaa: { enabled: false } },
    targetHeight: 480, pixelRatio: 0.5, upscale: 'performance', upscaleMode: 'fsr', cloudResolution: 0.25, adaptive: true, particleCap: 0, fogScale: 0.7, cloudsEnabled: false,
    shadowsEnabled: false, lightingBoost: 1.35,
    settlementRadiusScale: 0.5, settlementForceLowLod: true, distantDetail: false, placementLod: 3, settlementBuildLod: 2, riverWater: 'simple', groundQuality: 'simple', skyQuality: 'flat', look: 'none', foliageShading: false, fluidPhysics: false, materialFx: false, interiorMapping: false, vfxShaders: false, litDecals: false, lampCookies: false, waterFoam: false, waterCaustics: false, cloudShadows: false, groundAntiTiling: false, rainRipples: false, aurora: false, nebulae: false, planetAtmosphere: false, underwaterShafts: false, dqSkinning: false, specularAA: false, proceduralFiltering: false, volumetricLights: false, litSmoke: false, areaLights: false, glassTransmission: false, hardSurfaceAnisotropy: false, retroreflection: false, impostorBlend: false, rainShelter: false, autoExposureMetering: 'average', fireQuality: 'sprite', fireLights: 1, fireHaze: false,
    meshSmoothing: false, surfaceDetail: false, foliageDensityScale: 0,
    grassLayer: 'off', geologyDensity: 0, understoryDensity: 0, forestHorizonM: 0, farCanopy: false, foliageJitter: false, ambientLife: false, caveLight: 'visibility', // minimal = textures only (visual-realism R2–R12)
    materialDamage: 'off', // shader gaps R2: persistent material damage (lib/damage_*.ts)
    eonDiffuse: false, laplacianBlend: false, flakeGlints: false, // shader gaps R4: material models (lib/material_models.ts)
    windowShadows: false, // shader gaps R5: window lights shadowed by the people near their pane (lib/pane_visibility.ts)
    wetSurfaces: 'ground', surfaceDeform: false, // shader gaps R3: the wet clock + wet props, snow/mud that remember (lib/wet_*.ts, lib/deform_*.ts)
    lodDistanceScale: 0.5, shadowMapSize: 256, shadowCascades: 1, shadowMaxFar: 40,
    farCull: 500, chunkRadius: 2, foliageRing: 1, maxTextureRes: 512, nearFoliageLod: 2, propLod: 2, farBodyLod: 2, nearActors: 0, drawCallBudget: 1500, memoryBudgetMb: 96, prefetchRegions: 0,
    groundMaterial: 'v2', groundArrayRes: 512, groundPotatoSet: false, groundTriplanar: false, groundBands: 2, groundPom: 'off', groundNearSeg: 48,
    aerialEnabled: false, autoExposure: false, lowClouds: false, cloudParallax: 1.0, aaQuality: 2, autoExposureStrength: 1, anisotropy: 1,
  },
  potato: {
    post: {},
    targetHeight: 720, pixelRatio: 1, upscale: 'balanced', upscaleMode: 'fsr', cloudResolution: 0.25, adaptive: true, particleCap: 0, fogScale: 0.7, cloudsEnabled: false,
    shadowsEnabled: false, lightingBoost: 1.35,
    settlementRadiusScale: 0.6, settlementForceLowLod: true, distantDetail: true, placementLod: 2, settlementBuildLod: 1, riverWater: 'realistic', groundQuality: 'full', skyQuality: 'full', look: 'none', foliageShading: false, fluidPhysics: false, materialFx: false, interiorMapping: false, vfxShaders: false, litDecals: false, lampCookies: false, waterFoam: false, waterCaustics: false, cloudShadows: false, groundAntiTiling: false, rainRipples: false, aurora: false, nebulae: false, planetAtmosphere: false, underwaterShafts: false, dqSkinning: false, specularAA: false, proceduralFiltering: false, volumetricLights: false, litSmoke: false, areaLights: false, glassTransmission: false, hardSurfaceAnisotropy: false, retroreflection: false, impostorBlend: false, rainShelter: false, autoExposureMetering: 'average', fireQuality: 'sprite', fireLights: 1, fireHaze: false,
    meshSmoothing: false, surfaceDetail: false, foliageDensityScale: 0.35,
    grassLayer: 'cards', geologyDensity: 0.25, understoryDensity: 0.25, forestHorizonM: 0, farCanopy: true, foliageJitter: true, ambientLife: false, caveLight: 'visibility',
    materialDamage: 'off', // shader gaps R2: persistent material damage (lib/damage_*.ts)
    eonDiffuse: false, laplacianBlend: false, flakeGlints: false, // shader gaps R4: material models (lib/material_models.ts)
    windowShadows: false, // shader gaps R5: window lights shadowed by the people near their pane (lib/pane_visibility.ts)
    wetSurfaces: 'ground', surfaceDeform: false, // shader gaps R3: the wet clock + wet props, snow/mud that remember (lib/wet_*.ts, lib/deform_*.ts)
    lodDistanceScale: 1.0, shadowMapSize: 512, shadowCascades: 1, shadowMaxFar: 80,
    farCull: 500, chunkRadius: 3, foliageRing: 1, maxTextureRes: 512, nearFoliageLod: 2, propLod: 2, farBodyLod: 2, nearActors: 4, drawCallBudget: 2500, memoryBudgetMb: 128, prefetchRegions: 0,
    groundMaterial: 'v2', groundArrayRes: 512, groundPotatoSet: true, groundTriplanar: false, groundBands: 2, groundPom: 'off', groundNearSeg: 48,
    aerialEnabled: false, autoExposure: true, autoExposureRangeEv: 1, lowClouds: false, cloudParallax: 1.0, aaQuality: 2, autoExposureStrength: 1, anisotropy: 2, // metering ±1 EV around the curve (2026-09-25)
  },
  low: {
    // Lighting realism (2026-09-25, §8 R4 / §10 R5 / R11): a threshold-less, ENERGY-CONSERVING Kawase veil
    // (mix(scene, blur, 0.03), 5 levels — WebGL2 too) and a cos⁴-like 10 % vignette.
    post: { bloom: { mode: 'kawase', composite: 'lerp', strength: 0.03, radius: 1, passes: 5 }, fxaa: { enabled: true }, vignette: { strength: 0.1, radius: 0.25, softness: 0.5 } },
    targetHeight: 1080, pixelRatio: 1, upscale: 'quality', upscaleMode: 'fsr', cloudResolution: 0.25, adaptive: true, particleCap: 0.25, fogScale: 0.85, cloudsEnabled: true,
    shadowsEnabled: true, lightingBoost: 1,
    // ForceLowLod on: the low tier caps textures at 1024 anyway, so the 22 MB
    // LOD0 civ GLBs (16× 2K KTX2 inside) paid full download+decode for pixels
    // the tier then threw away — the 1.9 MB LOD1 variants ARE the 1024-class
    // set (11× less transfer; the Quest browser auto-detects this tier).
    settlementRadiusScale: 0.75, settlementForceLowLod: true, distantDetail: true, placementLod: 2, settlementBuildLod: 1, riverWater: 'realistic', groundQuality: 'full', skyQuality: 'full', look: 'natural', foliageShading: true, fluidPhysics: true, materialFx: false, interiorMapping: false, vfxShaders: true, litDecals: false, lampCookies: false, waterFoam: false, waterCaustics: false, cloudShadows: true, groundAntiTiling: false, rainRipples: true, aurora: false, nebulae: false, planetAtmosphere: false, underwaterShafts: false, dqSkinning: false, specularAA: true, proceduralFiltering: true, volumetricLights: false, litSmoke: false, areaLights: false, glassTransmission: false, hardSurfaceAnisotropy: false, retroreflection: false, impostorBlend: false, rainShelter: true, autoExposureMetering: 'average', fireQuality: 'cards', fireLights: 2, fireHaze: false,
    meshSmoothing: false, surfaceDetail: false, foliageDensityScale: 0.6,
    grassLayer: 'low', geologyDensity: 0.5, understoryDensity: 0.5, forestHorizonM: 0, farCanopy: true, foliageJitter: true, ambientLife: true, caveLight: 'baked',
    materialDamage: 'marks', // shader gaps R2: persistent material damage (lib/damage_*.ts)
    eonDiffuse: false, laplacianBlend: false, flakeGlints: false, // shader gaps R4: material models (lib/material_models.ts)
    windowShadows: false, // shader gaps R5: window lights shadowed by the people near their pane (lib/pane_visibility.ts)
    wetSurfaces: 'ground', surfaceDeform: false, // shader gaps R3: the wet clock + wet props, snow/mud that remember (lib/wet_*.ts, lib/deform_*.ts)
    lodDistanceScale: 1.6, shadowMapSize: 1024, shadowCascades: 2, shadowMaxFar: 150,
    farCull: 800, chunkRadius: 3, foliageRing: 2, maxTextureRes: 1024, nearFoliageLod: 2, propLod: 1, farBodyLod: 1, nearActors: 8, drawCallBudget: 5000, memoryBudgetMb: 256, prefetchRegions: 1,
    groundMaterial: 'v2', groundArrayRes: 512, groundPotatoSet: false, groundTriplanar: true, groundBands: 3, groundPom: 'off', groundNearSeg: 48,
    aerialEnabled: false, autoExposure: true, lowClouds: false, cloudParallax: 1.0, aaQuality: 2, autoExposureStrength: 1, anisotropy: 4, cloudMarchQuality: 'low',
  },
  med: {
    post: {
      smaa: { enabled: true, quality: 2 }, // quality = this tier's aaQuality: the PRESET must carry it or the pipeline never sees it (2026-09-15)
      // 2026-09-25 (§8 R4 / §10 R5 / R11): the energy-conserving Kawase veil replaces the thresholded additive
      // glow (threshold 1.1 made bloom binary — only emitters glowed); display-referred luma grain 1.5 % after
      // AA; a 12 % vignette.
      bloom: { mode: 'kawase', composite: 'lerp', strength: 0.04, radius: 1, passes: 5 },
      film: { intensity: 0.015, mode: 'luma' },
      vignette: { strength: 0.12, radius: 0.25, softness: 0.5 },
      // Ladder (2026-09-09): half-res GTAO at the XeGTAO low preset (8 spp) and the analytic height fog — cheap at 1440p.
      ao: { intensity: 0.9, radius: 0.45, samples: 8, resolutionScale: 0.5 },
      volumetricFog: { density: 0.010, heightFalloff: 0.05, sunPower: 6 },
    },
    targetHeight: 1440, pixelRatio: 1.5, upscale: 'quality', upscaleMode: 'fsr', cloudResolution: 0.5, adaptive: true, particleCap: 0.6, fogScale: 1.0, cloudsEnabled: true,
    shadowsEnabled: true, lightingBoost: 1,
    settlementRadiusScale: 1.25, settlementForceLowLod: false, distantDetail: true, placementLod: 1, settlementBuildLod: 1, riverWater: 'realistic', groundQuality: 'full', skyQuality: 'full', look: 'natural', foliageShading: true, fluidPhysics: true, materialFx: true, interiorMapping: true, vfxShaders: true, litDecals: true, lampCookies: false, waterFoam: false, waterCaustics: false, cloudShadows: true, groundAntiTiling: true, rainRipples: true, aurora: false, nebulae: false, planetAtmosphere: true, underwaterShafts: true, dqSkinning: false, specularAA: true, proceduralFiltering: true, volumetricLights: false, litSmoke: true, areaLights: false, glassTransmission: false, hardSurfaceAnisotropy: true, retroreflection: true, impostorBlend: true, rainShelter: true, autoExposureMetering: 'histogram', fireQuality: 'volume', fireLights: 2, fireHaze: false,
    meshSmoothing: true, surfaceDetail: true, foliageDensityScale: 0.85,
    grassLayer: 'med', geologyDensity: 0.75, understoryDensity: 0.75, forestHorizonM: 2500, farCanopy: true, foliageJitter: true, ambientLife: true, caveLight: 'baked+probe',
    materialDamage: 'marks', // shader gaps R2: persistent material damage (lib/damage_*.ts)
    eonDiffuse: true, laplacianBlend: false, flakeGlints: true, // shader gaps R4: material models (lib/material_models.ts)
    windowShadows: false, // shader gaps R5: window lights shadowed by the people near their pane (lib/pane_visibility.ts)
    wetSurfaces: 'all', surfaceDeform: false, // shader gaps R3: the wet clock + wet props, snow/mud that remember (lib/wet_*.ts, lib/deform_*.ts)
    lodDistanceScale: 2.2, shadowMapSize: 2048, shadowCascades: 3, shadowMaxFar: 200,
    farCull: 1300, chunkRadius: 5, foliageRing: 2, maxTextureRes: 2048, nearFoliageLod: 1, propLod: 1, farBodyLod: 1, nearActors: 12, drawCallBudget: 10000, memoryBudgetMb: 512, prefetchRegions: 1,
    groundMaterial: 'v2', groundArrayRes: 1024, groundPotatoSet: false, groundTriplanar: true, groundBands: 3, groundPom: 'off', groundNearSeg: 48,
    aerialEnabled: true, autoExposure: true, lowClouds: false, cloudParallax: 1.0, aaQuality: 2, autoExposureStrength: 1, anisotropy: 8,
  },
  high: {
    post: {
      smaa: { enabled: true, quality: 4 },
      bloom: { mode: 'kawase', composite: 'lerp', strength: 0.04, radius: 1, passes: 5 }, // the energy-conserving veil (2026-09-25)
      film: { intensity: 0.015, mode: 'luma' },
      vignette: { strength: 0.12, radius: 0.25, softness: 0.5 },
      // SSR on HIGH (2026-09-25, §3 / §8 R11: the default player's tier). Both backends since the gaps wave round 2 (the
      // engine's vendored SSR node compiles on WebGL2); `atlas:ssr-webgl2 = 'off'` drops it on the fallback.
      ssr: { intensity: 1.0, maxDistance: 40, thickness: 0.35, resolutionScale: 0.5 },
      // Capsule shadows ground the player where the CSM cannot (and are the only grounding under a storm deck, R12).
      capsuleShadows: { enabled: true },
      ao: { intensity: 1.0, radius: 0.5, samples: 16, resolutionScale: 0.5 }, // half-res GTAO (perf frontier item 10)
      // `focalLength` is the FOCUS RANGE in METRES, not a lens length: three
      // computes CoC = smoothstep(0, focalLength, |viewZ| − focusDistance), so
      // 0.05 meant "completely out of focus 5 cm off the 18 m plane" — i.e.
      // the whole world blurred on the DEFAULT tier (2026-09-14). 55 m keeps
      // the near foreground and the far distance soft while gameplay range
      // stays sharp.
      dof: { focusDistance: 18, focalLength: 55, bokehScale: 1.2 },
      // Shader wave 2026-09-08: the engine passes that were built and menu-exposed but off in every preset.
      volumetricFog: { density: 0.012, heightFalloff: 0.05, sunPower: 8 }, // analytic height fog + sun in-scatter
      contactShadows: { enabled: true },
      lensFlares: { enabled: true },
    },
    targetHeight: 1800, pixelRatio: 2, upscale: 'quality', upscaleMode: 'fsr', cloudResolution: 0.5, adaptive: true, particleCap: 1.0, fogScale: 0.6, cloudsEnabled: true,
    shadowsEnabled: true, lightingBoost: 1,
    settlementRadiusScale: 1.7, settlementForceLowLod: false, distantDetail: true, placementLod: 0, settlementBuildLod: 1, riverWater: 'realistic', groundQuality: 'full', skyQuality: 'full', look: 'natural', foliageShading: true, fluidPhysics: true, materialFx: true, interiorMapping: true, vfxShaders: true, litDecals: true, lampCookies: true, waterFoam: true, waterCaustics: false, cloudShadows: true, groundAntiTiling: true, rainRipples: true, aurora: true, nebulae: true, planetAtmosphere: true, underwaterShafts: true, dqSkinning: true, specularAA: true, proceduralFiltering: true, volumetricLights: true, litSmoke: true, areaLights: true, glassTransmission: true, hardSurfaceAnisotropy: true, retroreflection: true, impostorBlend: true, rainShelter: true, autoExposureMetering: 'histogram', fireQuality: 'volume', fireLights: 3, fireHaze: true,
    meshSmoothing: true, surfaceDetail: true, foliageDensityScale: 1.0,
    grassLayer: 'high', geologyDensity: 1, understoryDensity: 1, forestHorizonM: 4000, farCanopy: true, foliageJitter: true, ambientLife: true, caveLight: 'baked+probe',
    materialDamage: 'full', // shader gaps R2: persistent material damage (lib/damage_*.ts)
    eonDiffuse: true, laplacianBlend: false, flakeGlints: true, // shader gaps R4: material models (lib/material_models.ts)
    windowShadows: true, // shader gaps R5: window lights shadowed by the people near their pane (lib/pane_visibility.ts)
    wetSurfaces: 'all', surfaceDeform: true, // shader gaps R3: the wet clock + wet props, snow/mud that remember (lib/wet_*.ts, lib/deform_*.ts)
    lodDistanceScale: 3.0, shadowMapSize: 4096, shadowCascades: 4, shadowMaxFar: 300,
    farCull: 1800, chunkRadius: 7, foliageRing: 3, maxTextureRes: 2048, nearFoliageLod: 0, propLod: 0, farBodyLod: 0, nearActors: 18, drawCallBudget: 20000, memoryBudgetMb: 1024, prefetchRegions: 2,
    groundMaterial: 'v2', groundArrayRes: 2048, groundPotatoSet: false, groundTriplanar: true, groundBands: 3, groundPom: 'rock', groundNearSeg: 48,
    groundHexTiling: true, // surfaces (2026-09-27): §2.3 Mikkelsen hex-tiling on the dominant layer (3 rotated fetches) at high / ultra
    aerialEnabled: true, autoExposure: true, lowClouds: true, cloudParallax: 1.0, aaQuality: 4, autoExposureStrength: 1, anisotropy: 16,
  },
  ultra: {
    post: {
      smaa: { enabled: true, quality: 4 },
      bloom: { mode: 'kawase', composite: 'lerp', strength: 0.05, radius: 1.2, passes: 5 }, // the energy-conserving veil (2026-09-25)
      film: { intensity: 0.015, mode: 'luma' },
      vignette: { strength: 0.12, radius: 0.25, softness: 0.5 },
      capsuleShadows: { enabled: true },
      ao: { intensity: 1.1, radius: 0.6, samples: 24, resolutionScale: 0.5 },
      dof: { focusDistance: 18, focalLength: 45, bokehScale: 1.4 }, // focus RANGE in metres — see the high tier
      // Shader wave 2026-09-08: everything the engine has, on at ultra.
      volumetricFog: { density: 0.012, heightFalloff: 0.05, sunPower: 8 },
      contactShadows: { enabled: true },
      lensFlares: { enabled: true },
      ssr: { intensity: 1.0, maxDistance: 40, thickness: 0.35, resolutionScale: 0.5 }, // 1.0 (was 0.6): the composite REPLACES the IBL specular on a hit (render wave 1), the damping compensated for a double count
      godRays: { enabled: true, numSamples: 48, decay: 0.965, exposure: 0.22, density: 1.0, weight: 0.01 },
      // `temporal: false` (2026-09-14). three's SSGINode uses temporal filtering
      // to JITTER its slice direction and spatial offset per frame (a 6-frame
      // rotation, a 4-frame offset) on the assumption that TRAA resolves the
      // result. This tier antialiases with SMAA and allocates no velocity
      // target, so nothing ever averaged those frames — the jitter just
      // shimmered. Pinned sampling is stable instead. Flip back to true only
      // together with TRAA (which additionally needs real per-instance motion
      // vectors — the crowd instancer currently pins them to zero).
      // NB the comment's "half-res" was never true either: SSGINode re-sizes
      // its target to the full drawing buffer in updateBefore and exposes no
      // resolution scale.
      ssgi: { enabled: true, sliceCount: 2, stepCount: 8, temporal: false, intensity: 1, aoStrength: 0.5 },
    },
    targetHeight: 2160, pixelRatio: 2, upscale: 'ultra', upscaleMode: 'fsr', cloudResolution: 0.75, adaptive: true, particleCap: 1.0, fogScale: 0.45, cloudsEnabled: true,
    shadowsEnabled: true, lightingBoost: 1,
    settlementRadiusScale: 2.5, settlementForceLowLod: false, distantDetail: true, placementLod: 0, settlementBuildLod: 1, riverWater: 'realistic', groundQuality: 'full', skyQuality: 'full', look: 'natural', foliageShading: true, fluidPhysics: true, materialFx: true, interiorMapping: true, vfxShaders: true, litDecals: true, lampCookies: true, waterFoam: true, waterCaustics: true, cloudShadows: true, groundAntiTiling: true, rainRipples: true, aurora: true, nebulae: true, planetAtmosphere: true, underwaterShafts: true, dqSkinning: true, specularAA: true, proceduralFiltering: true, volumetricLights: true, litSmoke: true, areaLights: true, glassTransmission: true, hardSurfaceAnisotropy: true, retroreflection: true, impostorBlend: true, rainShelter: true, autoExposureMetering: 'histogram', fireQuality: 'volume' /* coordinator (2026-09-27): the SIM rung drew a glow with no flame body (the gas never rose — fire CHECKPOINT 3). 2026-09-29 (shader gaps): the transport is FIXED (THE FLOOR IS THE GROUND, lib/vfx/fire_sim.ts — verify:fire-sim-bed:probe) but its plume still leans into the 1.3 m box and clips at a wall; ultra keeps the volume flame, 'sim' stays selectable */, fireLights: 3, fireHaze: true,
    meshSmoothing: true, surfaceDetail: true, foliageDensityScale: 1.15,
    grassLayer: 'ultra', geologyDensity: 1.25, understoryDensity: 1.25, forestHorizonM: 8000, farCanopy: true, foliageJitter: true, ambientLife: true, caveLight: 'baked+probe',
    materialDamage: 'full', // shader gaps R2: persistent material damage (lib/damage_*.ts)
    eonDiffuse: true, laplacianBlend: false /* measured no better than the height blend — an opt-in (impl-material-models.md) */, flakeGlints: true, // shader gaps R4: material models (lib/material_models.ts)
    windowShadows: true, // shader gaps R5: window lights shadowed by the people near their pane (lib/pane_visibility.ts)
    wetSurfaces: 'all', surfaceDeform: true, // shader gaps R3: the wet clock + wet props, snow/mud that remember (lib/wet_*.ts, lib/deform_*.ts)
    lodDistanceScale: 4.0, shadowMapSize: 4096, shadowCascades: 4, shadowMaxFar: 400,
    // DRAW DISTANCE HALVED (2026-09-17, "full draw distance is too far"): ultra 20 → 10 chunks (2.56 km),
    // and every tier below scaled with it (high 12 → 7, med 6 → 5, low 4 → 3; potato/minimal at the 500 m floor);
    // farCull follows the window, and settlementRadiusScale keeps a city's build band INSIDE the window.
    // The Draw-distance slider still reaches 5 km (setDrawDistance).
    farCull: 2560, chunkRadius: 10, foliageRing: 3, maxTextureRes: 4096, nearFoliageLod: 0, propLod: 0, farBodyLod: 0, nearActors: 24, drawCallBudget: 40000, memoryBudgetMb: 2048, prefetchRegions: 3,
    // ground (2026-09-26): ultra wears the 8192 set where the backend holds it (the ladder drops to 4096 / 2048 otherwise).
    groundMaterial: 'v2', groundArrayRes: 8192, groundPotatoSet: false, groundTriplanar: true, groundBands: 3, groundPom: 'all', groundNearSeg: 48,
    groundHexTiling: true, // surfaces (2026-09-27): §2.3 hex-tiling (see high)
    aerialEnabled: true, autoExposure: true, lowClouds: true, cloudParallax: 1.0, aaQuality: 4, autoExposureStrength: 1, anisotropy: 16, waterReflections: true, cloudMarchQuality: 'high',
  },
};

// ── Water realism wave (2026-09-25): the water fields per tier, merged into TIERS here (one
// insertion instead of six edits to the long preset rows). Monotone up the ladder (verify:preset-ladder).
export type WaterLookKey =
  | 'waterSkyReflection' | 'waterCloudReflection' | 'waterSpectrumNormals' | 'waterScattering' | 'waterGlitterFilter'
  | 'waterFarSea' | 'waterAnalyticBed' | 'waterBedCaustics' | 'waterUnderwaterMedium' | 'waterMarineLife'
  | 'waterCalmLakes' | 'waterfallFx' | 'waterReflectionBudget' | 'waterSplashCrowns' | 'waterFoamField' | 'waterRiverFlow';
export const WATER_TIERS: Record<Tier, Record<WaterLookKey, boolean>> = {
  minimal: { waterSkyReflection: true, waterCloudReflection: false, waterSpectrumNormals: true, waterScattering: false, waterGlitterFilter: true, waterFarSea: false, waterAnalyticBed: false, waterBedCaustics: false, waterUnderwaterMedium: false, waterMarineLife: false, waterCalmLakes: true, waterfallFx: false, waterReflectionBudget: false, waterSplashCrowns: false, waterFoamField: false, waterRiverFlow: false },
  potato: { waterSkyReflection: true, waterCloudReflection: false, waterSpectrumNormals: true, waterScattering: false, waterGlitterFilter: true, waterFarSea: true, waterAnalyticBed: false, waterBedCaustics: false, waterUnderwaterMedium: false, waterMarineLife: false, waterCalmLakes: true, waterfallFx: false, waterReflectionBudget: false, waterSplashCrowns: false, waterFoamField: false, waterRiverFlow: false },
  low: { waterSkyReflection: true, waterCloudReflection: false, waterSpectrumNormals: true, waterScattering: true, waterGlitterFilter: true, waterFarSea: true, waterAnalyticBed: true, waterBedCaustics: false, waterUnderwaterMedium: false, waterMarineLife: false, waterCalmLakes: true, waterfallFx: true, waterReflectionBudget: false, waterSplashCrowns: true, waterFoamField: false, waterRiverFlow: true },
  med: { waterSkyReflection: true, waterCloudReflection: true, waterSpectrumNormals: true, waterScattering: true, waterGlitterFilter: true, waterFarSea: true, waterAnalyticBed: true, waterBedCaustics: true, waterUnderwaterMedium: true, waterMarineLife: true, waterCalmLakes: true, waterfallFx: true, waterReflectionBudget: false, waterSplashCrowns: true, waterFoamField: false, waterRiverFlow: true },
  high: { waterSkyReflection: true, waterCloudReflection: true, waterSpectrumNormals: true, waterScattering: true, waterGlitterFilter: true, waterFarSea: true, waterAnalyticBed: true, waterBedCaustics: true, waterUnderwaterMedium: true, waterMarineLife: true, waterCalmLakes: true, waterfallFx: true, waterReflectionBudget: false, waterSplashCrowns: true, waterFoamField: true, waterRiverFlow: true },
  ultra: { waterSkyReflection: true, waterCloudReflection: true, waterSpectrumNormals: true, waterScattering: true, waterGlitterFilter: true, waterFarSea: true, waterAnalyticBed: true, waterBedCaustics: true, waterUnderwaterMedium: true, waterMarineLife: true, waterCalmLakes: true, waterfallFx: true, waterReflectionBudget: true, waterSplashCrowns: true, waterFoamField: true, waterRiverFlow: true },
};
for (const t of Object.keys(WATER_TIERS) as Tier[]) Object.assign(TIERS[t], WATER_TIERS[t]);

// ── Visual-realism wave: materials / AA / camera / characters (2026-09-25) — one merge, like the water fields.
//   • AA BEFORE UPSCALE on the spatial tiers (AMD's FSR 1 order) with AMD's real EASU kernel;
//   • TAAU (three r185's TAAUNode: jittered low-res pass → output history) on high / ultra — WebGPU only, the
//     engine keeps the SMAA → FSR path on WebGL2 — which is what finally resolves sub-pixel foliage;
//     SSGI's temporal jitter becomes valid at ultra (the resolve now averages it);
//   • motion blur at high / ultra: a 180° shutter, camera-induced blur at half weight (now that the crowd and
//     DQ actors carry real motion vectors);
//   • baked AO on the finished assets → GTAO eased to 0.6 on med+ so the two never double-darken a crevice;
//   • DOF: third-person only, a physical 35 mm f/5.6 lens autofocused on the player (the high/ultra 18 m / 55 m
//     range blurred every vista past ~73 m, in first person too).
const REALISM_MATERIAL_TIERS: Record<Tier, Partial<TierPreset>> = {
  minimal: { materialGrime: false, mipBias: false, cameraDof: 'off', cameraFNumber: 5.6, playerBodyRig: false, footIk: false, breathing: false },
  potato: { materialGrime: false, mipBias: true, cameraDof: 'off', cameraFNumber: 5.6, playerBodyRig: true, footIk: false, breathing: true },
  low: { materialGrime: false, mipBias: true, cameraDof: 'off', cameraFNumber: 5.6, playerBodyRig: true, footIk: true, breathing: true },
  med: { materialGrime: true, mipBias: true, cameraDof: 'off', cameraFNumber: 5.6, playerBodyRig: true, footIk: true, breathing: true },
  high: { materialGrime: true, mipBias: true, cameraDof: 'thirdPerson', cameraFNumber: 5.6, playerBodyRig: true, footIk: true, breathing: true, upscaleMode: 'taau' },
  ultra: { materialGrime: true, mipBias: true, cameraDof: 'thirdPerson', cameraFNumber: 5.6, playerBodyRig: true, footIk: true, breathing: true, upscaleMode: 'taau' },
};
/** The lens every tier's DOF uses (render/dof/PhysicalDof.ts): 35 mm on a 36 mm sensor; the f-number is the tier's. */
export const CAMERA_LENS = { focalMm: 35, sensorMm: 36 } as const;
for (const t of Object.keys(REALISM_MATERIAL_TIERS) as Tier[]) {
  const p = TIERS[t];
  Object.assign(p, REALISM_MATERIAL_TIERS[t]);
  const post = p.post as Record<string, unknown> & { ao?: { intensity?: number }; dof?: Record<string, unknown>; ssgi?: Record<string, unknown> };
  post.upscaleOrder = { aaFirst: true, kernel: 'amd' };
  if (post.ao && t !== 'minimal' && t !== 'potato' && t !== 'low') post.ao = { ...post.ao, intensity: 0.6 };
  if (t === 'high' || t === 'ultra') {
    post.motionBlur = { samples: 12, shutterFraction: 0.5, cameraWeight: 0.5, objectWeight: 1 };
    if (post.dof) post.dof = { ...post.dof, focusDistance: 3, aperture: { fNumber: p.cameraFNumber ?? 5.6, ...CAMERA_LENS } };
  }
  if (t === 'ultra' && post.ssgi) post.ssgi = { ...post.ssgi, temporal: true };
}

/**
 * The space realism wave's per-tier switches (2026-09-25, the space agent). Kept as one table merged into TIERS
 * (the tier lines above are long and contended): the sky and the speed cues run everywhere (the catalogue loop is
 * capped per tier by memory); plumes, lit hulls and the shield/wound layer are low+ (report §9 R5/R6/R8).
 */
export const SPACE_TIER_FLAGS: Record<Tier, Pick<TierPreset, 'spaceSky' | 'spaceMotion' | 'spacePlumes' | 'spaceLitHulls' | 'spaceShields'>> = {
  minimal: { spaceSky: true, spaceMotion: true, spacePlumes: false, spaceLitHulls: false, spaceShields: false },
  potato:  { spaceSky: true, spaceMotion: true, spacePlumes: false, spaceLitHulls: false, spaceShields: false },
  low:     { spaceSky: true, spaceMotion: true, spacePlumes: true, spaceLitHulls: true, spaceShields: true },
  med:     { spaceSky: true, spaceMotion: true, spacePlumes: true, spaceLitHulls: true, spaceShields: true },
  high:    { spaceSky: true, spaceMotion: true, spacePlumes: true, spaceLitHulls: true, spaceShields: true },
  ultra:   { spaceSky: true, spaceMotion: true, spacePlumes: true, spaceLitHulls: true, spaceShields: true },
};
for (const t of Object.keys(SPACE_TIER_FLAGS) as Tier[]) Object.assign(TIERS[t], SPACE_TIER_FLAGS[t]);
// sky/post (2026-09-27): §1.5 THE MOON casts its own shadow map on high / ultra only (the light exists on every tier at
// intensity 0 by day — the light-set identity law; below high a moonlit night reads by its direction and the floor).
for (const t of Object.keys(TIERS) as Tier[]) TIERS[t].moonShadows = t === 'high' || t === 'ultra';
// shader gaps R2 (2026-09-29): skin shading on the near rigged actors — high / ultra (lib/skin_*.ts)
for (const t of Object.keys(TIERS) as Tier[]) TIERS[t].skinShading = t === 'high' || t === 'ultra';
// shader gaps R3 (agent F): the light volume's resolution and jitter (only read where `volumetricLights` is on — high / ultra)
for (const t of Object.keys(TIERS) as Tier[]) { TIERS[t].volumeResolution = 'half'; TIERS[t].volumeNoise = 'golden'; }
// shader gaps R4 (agent G, space light): eclipses and rings from LOW up (the lit-hull tiers — minimal / potato keep the old
// look), the orbital cloud shell from MED up (a worker bake a minute; planetAtmosphere's tier)
for (const t of Object.keys(TIERS) as Tier[]) { const lowUp = t !== 'minimal' && t !== 'potato'; TIERS[t].eclipseShadows = lowUp; TIERS[t].planetRings = lowUp; TIERS[t].orbitalClouds = lowUp && t !== 'low'; }
// shader gaps R5 (agent I): the FFT ocean on ultra (WebGPU only — lib/fft_ocean.ts decides; the Gerstner sea elsewhere)
for (const t of Object.keys(TIERS) as Tier[]) TIERS[t].fftOcean = t === 'ultra';
// shader gaps: black hole (agent K) — the lens's tables and star samples by tier: off on minimal (the old look), low tables
// from potato to med, high tables + the 2×2 star supersample on high / ultra. Drawn only while a hole is placed.
for (const t of Object.keys(TIERS) as Tier[]) TIERS[t].blackHole = t === 'minimal' ? 'off' : t === 'high' || t === 'ultra' ? 'high' : 'low';
// shader gaps R6 (agent L): the shallow-water puddle patch — 64 m on med, 96 m on high, 128 m on ultra (WebGPU only; lib/puddle_sim.ts decides)
for (const t of Object.keys(TIERS) as Tier[]) TIERS[t].puddleSim = t === 'med' ? 'small' : t === 'high' ? 'medium' : t === 'ultra' ? 'large' : 'off';
// interiors programme, slice D: the interior quality overlay — essential on minimal / potato (128 px tiles, no wear, the
// generic room), standard on low / med, full on high / ultra. Topology, anchors, seats and owned pieces are the same on all.
for (const t of Object.keys(TIERS) as Tier[]) TIERS[t].interiorDetail = t === 'minimal' || t === 'potato' ? 0 : t === 'low' || t === 'med' ? 1 : 2;

export const TIER_ORDER: Tier[] = ['minimal', 'potato', 'low', 'med', 'high', 'ultra'];
export const TIER_LABEL: Record<Tier, string> = { minimal: 'Minimal', potato: 'Potato', low: 'Low', med: 'Medium', high: 'High', ultra: 'Ultra' };
export const DEFAULT_TIER: Tier = 'high';
export function nextTier(t: Tier): Tier { const i = TIER_ORDER.indexOf(t); return TIER_ORDER[(i + 1) % TIER_ORDER.length]; }

/** Deep-enough clone so per-feature mutations never write back into the shared TIERS preset. */
function clonePreset(p: TierPreset): TierPreset {
  return { ...p, post: { ...p.post } };
}

// ── Live active settings ──────────────────────────────────────────────────────
// `settings_` is the active TierPreset — a copy of the chosen preset, mutated in
// place by the per-feature setters. `custom_` flips true once any feature differs
// from a clean preset. `baseTier_` is the preset the customisation started from.
let baseTier_: Tier = DEFAULT_TIER;
let custom_ = false;
let settings_: TierPreset = clonePreset(TIERS[DEFAULT_TIER]);

/** The active (possibly customised) settings — every graphics consumer reads this. */
export function activePreset(): TierPreset { return settings_; }
/** The base preset the active settings derive from. */
export function activeTier(): Tier { return baseTier_; }
/** True when an individual feature was tweaked away from a clean preset. */
export function isCustom(): boolean { return custom_; }

/** The preferences a preset does not carry — they survive a preset switch AND an uncustomised load. */
const STICKY_KEYS = ['autoTune', 'colorblindMode', 'colorblindStrength', 'cameraFov', 'tonemapOperator', 'tonemapExposure', 'exposureMode'] as const;
function withSticky(preset: TierPreset, from: Record<string, unknown> | undefined): TierPreset {
  const out = preset as unknown as Record<string, unknown>;
  for (const k of STICKY_KEYS) if (from?.[k] !== undefined) out[k] = from[k];
  return preset;
}

/** Apply a clean preset (clears any per-feature customisation). */
export function setActiveTier(t: Tier): void {
  // Session-sticky preferences survive a preset switch (presets don't carry
  // them): auto-tune, colourblind correction, FOV, tone-map taste.
  const keep = settings_ as unknown as Record<string, unknown> | undefined;
  baseTier_ = t;
  custom_ = false;
  settings_ = withSticky(clonePreset(TIERS[t]), keep);
  saveSettings();
}


// ── The graphics DRAFT — what the Esc menu's "Apply changes" button applies (2026-09-20) ──────────────
// WHY: every graphics change is a post-pipeline REBUILD (TSL is compiled per configuration) plus an
// `atlas:quality-changed` (warm assets evicted, the sky LUTs re-baked) — and ~35 flag rows never announced
// themselves at all, so a toggle "did nothing" until some other row happened to rebuild. The menu now edits a
// DRAFT and one button applies it: one rebuild for any number of changes, and nothing half-applied.
//
// HOW: `settings_` stays THE LIVE OBJECT for the 47 modules that read it. The draft is swapped IN only for the
// duration of a synchronous callback (`inDraft`): the menu's render and its setter calls run inside it, so every
// existing setter, every `current*()` read and `setActiveTier` work on the draft UNCHANGED — a staged preset even
// shows its own values in the rows. Nothing else can run during the swap (one thread, no awaits), and
// `saveSettings` stands down inside it, so an un-applied draft is never persisted or seen by the game.
interface DraftState { baseTier: Tier; custom: boolean; settings: TierPreset }
let draft_: DraftState | null = null;
let draftBase_: DraftState | null = null; // the live state the draft was cut from — what "changed" is measured against
let inDraft_ = false;
let liveStamp_ = 0;        // bumped by every LIVE mutation (auto-tune, a probe lever, the tours)
let draftStamp_ = -1;

const snapshotLive = (): DraftState => ({ baseTier: baseTier_, custom: custom_, settings: clonePreset(settings_) });
const same = (a: unknown, b: unknown): boolean => a === b || JSON.stringify(a) === JSON.stringify(b);

/** The keys a draft changed against a base: top-level settings, `post.<pass>`, and `preset` for a tier switch. */
function diffKeys(d: DraftState, base: DraftState): string[] {
  const out: string[] = [];
  if (d.baseTier !== base.baseTier || (base.custom && !d.custom)) out.push('preset');
  const a = d.settings as unknown as Record<string, unknown>, b = base.settings as unknown as Record<string, unknown>;
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) {
    if (k === 'post') continue;
    if (!same(a[k], b[k])) out.push(k);
  }
  const pa = d.settings.post as unknown as Record<string, unknown>, pb = base.settings.post as unknown as Record<string, unknown>;
  for (const k of new Set([...Object.keys(pa), ...Object.keys(pb)])) if (!same(pa[k], pb[k])) out.push(`post.${k}`);
  return out;
}

/** Live settings moved under an open draft (auto-tune stepped the tier, a lever fired): keep what the PLAYER changed, take the rest from live. */
function rebaseDraft(): void {
  if (!draft_ || !draftBase_ || draftStamp_ === liveStamp_) return;
  const changed = diffKeys(draft_, draftBase_);
  const next = snapshotLive();
  if (changed.includes('preset')) { next.baseTier = draft_.baseTier; next.custom = draft_.custom; next.settings = clonePreset(draft_.settings); }
  else {
    const to = next.settings as unknown as Record<string, unknown>, from = draft_.settings as unknown as Record<string, unknown>;
    for (const k of changed) {
      if (k.startsWith('post.')) { const pk = k.slice(5); const v = (from.post as Record<string, unknown>)[pk]; if (v === undefined) delete (to.post as Record<string, unknown>)[pk]; else (to.post as Record<string, unknown>)[pk] = v; }
      else to[k] = from[k];
    }
    // Auto-tune persists WITHOUT marking custom (setAutoTune): a staged auto-tune toggle alone must not
    // turn a re-based draft "customised" — Apply would save custom, and the tuner never walks a custom profile.
    if (changed.some((k) => k !== 'autoTune')) next.custom = true;
  }
  draftBase_ = snapshotLive(); draft_ = next; draftStamp_ = liveStamp_;
}

/** Run `fn` with the DRAFT as the active settings (setters and reads alike). Opens a draft on first use. */
export function inDraft<T>(fn: () => T): T {
  if (inDraft_) return fn();
  if (!draft_) { draft_ = snapshotLive(); draftBase_ = snapshotLive(); draftStamp_ = liveStamp_; }
  rebaseDraft();
  const live: DraftState = { baseTier: baseTier_, custom: custom_, settings: settings_ };
  baseTier_ = draft_.baseTier; custom_ = draft_.custom; settings_ = draft_.settings; inDraft_ = true; liveHold_ = live;
  try { return fn(); }
  finally {
    draft_ = { baseTier: baseTier_, custom: custom_, settings: settings_ }; // setActiveTier REPLACES the object
    baseTier_ = live.baseTier; custom_ = live.custom; settings_ = live.settings; inDraft_ = false; liveHold_ = null;
  }
}
/** Inside `inDraft`, read something off the LIVE settings (a row's "was …"). Outside it, just runs `fn`. */
export function inLive<T>(fn: () => T): T {
  if (!inDraft_ || !draft_) return fn();
  const d: DraftState = { baseTier: baseTier_, custom: custom_, settings: settings_ };
  const live = liveHold_!;
  baseTier_ = live.baseTier; custom_ = live.custom; settings_ = live.settings;
  try { return fn(); } finally { baseTier_ = d.baseTier; custom_ = d.custom; settings_ = d.settings; }
}
let liveHold_: DraftState | null = null;
/** What the draft would change, as setting keys ('shadowMapSize', 'post.bloom', 'preset'). Empty = nothing to apply. */
export function draftChanges(): string[] { rebaseDraft(); return draft_ && draftBase_ ? diffKeys(draft_, draftBase_) : []; }
/** The draft becomes the live settings and is persisted. The caller rebuilds the pipeline (PostModule.applySettings). */
export function commitDraft(): boolean {
  const changed = draftChanges();
  if (!draft_ || changed.length === 0) { draft_ = null; draftBase_ = null; return false; }
  baseTier_ = draft_.baseTier; custom_ = draft_.custom; settings_ = draft_.settings;
  draft_ = null; draftBase_ = null; liveStamp_++;
  saveSettings();
  return true;
}
/** Throw the draft away: the menu shows the live settings again. */
export function discardDraft(): void { draft_ = null; draftBase_ = null; }

// ── Per-feature setters (Esc menu "Custom") ───────────────────────────────────
// Each mutates the live settings + flips `custom_` + persists. PostModule.applySettings()
// rebuilds the pipeline + re-emits atlas:quality-changed after any of these.

export type AAMode = 'off' | 'fxaa' | 'smaa' | 'traa';
export type ShadowLevel = 'off' | 'low' | 'med' | 'high' | 'ultra';

function markCustom(): void { custom_ = true; saveSettings(); }

/** The config a post pass comes back ON with (bug sweep 2026-09-23): what the LIVE settings run — so a
 *  draft's off → on is no change at all — else the tier's own preset config, else the setter's generic
 *  default. The generic defaults dropped the preset's tuning: AO off → on lost high's half-resolution GTAO
 *  (~4× the cost, and a "1 change waiting" no row showed), SSGI lost ultra's `temporal: false` (the shimmer
 *  that preset fixed came back). Shallow-copied: a pass config is flat, and nothing shares it with live. */
function passBack<K extends keyof PostProcessingConfig>(key: K, fallback: NonNullable<PostProcessingConfig[K]>): NonNullable<PostProcessingConfig[K]> {
  const src = inLive(() => settings_.post[key]) ?? TIERS[baseTier_].post[key] ?? fallback;
  return { ...(src as object) } as NonNullable<PostProcessingConfig[K]>;
}

/** Render-scale (the pixel-ratio cap) — the single biggest perf lever. 0.5..2.0. */
/**
 * THE render-scale rule (2026-09-08, "fix the render scale cap so the canvas
 * actually shrinks on potato"): the tier's cap clamped to the display's device
 * pixel ratio — ultra alone may exceed it (true supersampling). ONE function so
 * the boot config (App.create), PostModule's apply and every re-apply agree.
 * Measured before: the cap landed only inside the first pipeline build, AFTER
 * the pipeline had measured the uncapped buffer — the menu and the world
 * streaming behind it rendered at the full display ratio.
 */
export function effectivePixelRatio(
  dpr: number = (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1,
  cssHeight: number = (typeof window !== 'undefined' ? window.innerHeight : 1080) || 1080,
): number {
  // Resolution TARGET mode (the tiers' default): the canvas height the tier
  // wants, whatever the display — "potato and minimal very low, ultra 4K".
  const target = settings_.targetHeight ?? 0;
  if (target > 0) return clampNum(target / Math.max(1, cssHeight), MIN_RENDER_SCALE, MAX_RENDER_SCALE);
  // Ratio mode (the Render-scale slider): the cap clamped to the display, ultra alone above it.
  const cap = settings_.pixelRatio;
  return activeTier() === 'ultra' ? cap : Math.min(dpr, cap);
}
/** The canvas height effectivePixelRatio yields for a window (probes/menus). */
export function effectiveCanvasHeight(cssHeight: number, dpr = 1): number { return Math.floor(cssHeight * effectivePixelRatio(dpr, cssHeight)); }
export const MIN_RENDER_SCALE = 0.2; // minimal at 480p on a 4K CSS window needs 0.22
export const MAX_RENDER_SCALE = 3; // ultra's 4K on a 720-px-high window
export const RESOLUTION_TARGETS: Array<{ height: number; label: string }> = [
  { height: 480, label: '480p' }, { height: 720, label: '720p' }, { height: 1080, label: '1080p' }, { height: 1440, label: '1440p' }, { height: 1800, label: '1800p' }, { height: 2160, label: '4K' }, { height: 0, label: 'Native' },
];
/** Resolution target (canvas height, 0 = native) — see TierPreset.targetHeight. */
export function setTargetHeight(h: number): void { settings_.targetHeight = clampNum(Math.round(h), 0, 4320); markCustom(); } // minimal tier (2026-09-08): with FSR at 2× this is 0.175 of the display per axis
export function setRenderScale(v: number): void { settings_.pixelRatio = clampNum(v, MIN_RENDER_SCALE, 2); settings_.targetHeight = 0; markCustom(); } // the slider = ratio mode
export function setUpscale(m: UpscaleMode): void { settings_.upscale = UPSCALE_RATIO[m] !== undefined ? m : 'off'; markCustom(); }
export function setAdaptive(on: boolean): void { settings_.adaptive = on; markCustom(); }
/** Distant detail (HLOD / far terrain past the draw distance) on or off — see TierPreset.distantDetail. */
export function setDistantDetail(on: boolean): void { settings_.distantDetail = on; markCustom(); }
/** True unless the player turned distant detail off (older saved settings lack the field = on). */
export function distantDetailOn(): boolean { return settings_.distantDetail !== false; }
export function setPlacementLod(l: 0 | 1 | 2 | 3): void { settings_.placementLod = l; markCustom(); }
export function setSettlementBuildLod(l: 1 | 2): void { settings_.settlementBuildLod = l; markCustom(); }
export function setRiverWater(m: 'realistic' | 'simple'): void { settings_.riverWater = m; markCustom(); }
export function setGroundQuality(q: 'full' | 'simple'): void { settings_.groundQuality = q; markCustom(); }
// ── Ground material v2 (2026-09-25) ──
export type GroundMaterial = 'v1' | 'v2';
/** The resolved ground knobs of a preset (the fields are optional: an older saved profile falls back to its tier's). */
export function groundKnobs(p: TierPreset = settings_): { material: GroundMaterial; res: 512 | 1024 | 2048 | 4096 | 8192; potato: boolean; triplanar: boolean; bands: 2 | 3; pom: 'off' | 'rock' | 'all'; nearSeg: 48 | 96; antiTiling: boolean; simple: boolean; microShadow: boolean; hexTiling: boolean; pomShadow: boolean } {
  const t = TIERS[baseTier_] ?? TIERS.med;
  return {
    material: p.groundMaterial ?? t.groundMaterial ?? 'v2',
    res: p.groundArrayRes ?? t.groundArrayRes ?? 1024,
    potato: p.groundPotatoSet ?? t.groundPotatoSet ?? false,
    triplanar: p.groundTriplanar ?? t.groundTriplanar ?? true,
    bands: p.groundBands ?? t.groundBands ?? 3,
    pom: p.groundPom ?? t.groundPom ?? 'off',
    nearSeg: p.groundNearSeg ?? t.groundNearSeg ?? 48,
    antiTiling: !!p.groundAntiTiling,
    simple: (p.groundQuality ?? 'full') === 'simple',
    // surfaces (2026-09-27): the three new ground knobs (see TierPreset.groundMicroShadow)
    microShadow: p.groundMicroShadow ?? t.groundMicroShadow ?? true,
    hexTiling: p.groundHexTiling ?? t.groundHexTiling ?? false,
    pomShadow: p.groundPomShadow ?? t.groundPomShadow ?? false,
  };
}
export function setGroundMicroShadow(on: boolean): void { settings_.groundMicroShadow = on; markCustom(); } // surfaces (2026-09-27)
export function setGroundHexTiling(on: boolean): void { settings_.groundHexTiling = on; markCustom(); } // surfaces (2026-09-27)
export function setGroundPomShadow(on: boolean): void { settings_.groundPomShadow = on; markCustom(); } // surfaces (2026-09-27)
export function setGroundMaterial(v: GroundMaterial): void { settings_.groundMaterial = v; markCustom(); }
export function setGroundArrayRes(v: 512 | 1024 | 2048 | 4096 | 8192): void { settings_.groundArrayRes = v; settings_.groundPotatoSet = false; markCustom(); }
export function setGroundTriplanar(on: boolean): void { settings_.groundTriplanar = on; markCustom(); }
export function setGroundPom(v: 'off' | 'rock' | 'all'): void { settings_.groundPom = v; markCustom(); }
export function setGroundNearSeg(v: 48 | 96): void { settings_.groundNearSeg = v; markCustom(); }
export function setSkyQuality(q: 'full' | 'flat'): void { settings_.skyQuality = q; markCustom(); }
export function setUpscaleMode(m: 'fsr' | 'taau'): void { settings_.upscaleMode = m === 'taau' ? 'taau' : 'fsr'; markCustom(); }
export function setCloudResolution(v: number): void { settings_.cloudResolution = clampNum(v, 0.25, 1); markCustom(); }
export function setAA(mode: AAMode): void {
  const post = { ...settings_.post };
  delete post.fxaa; delete post.smaa; delete post.traa;
  if (mode === 'fxaa') post.fxaa = { enabled: true, quality: settings_.aaQuality };
  else if (mode === 'smaa') post.smaa = { enabled: true, quality: settings_.aaQuality };
  // TRAA needs the velocity MRT; the pipeline falls back to FXAA when the
  // target is unavailable (its own guard) — safe to request unconditionally.
  else if (mode === 'traa') post.traa = { enabled: true, quality: settings_.aaQuality };
  // 'off' must be EXPLICIT: the engine applies FXAA by default unless
  // `fxaa.enabled === false` (PostProcessingPipeline 24c) — deleting both keys
  // silently left FXAA running while the menu (and currentAA) claimed "off".
  else post.fxaa = { enabled: false };
  settings_.post = post; markCustom();
}
export function setBloom(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.bloom = post.bloom ?? passBack('bloom', { mode: 'kawase', composite: 'lerp', strength: 0.04, radius: 1, passes: 5 });
  else delete post.bloom;
  settings_.post = post; markCustom();
}
export function setSSAO(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.ao = post.ao ?? passBack('ao', { intensity: 1.0, radius: 0.5, samples: 16 });
  else delete post.ao;
  settings_.post = post; markCustom();
}
export function setDoF(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.dof = post.dof ?? passBack('dof', { focusDistance: 18, focalLength: 55, bokehScale: 1.2 });
  else delete post.dof;
  settings_.post = post; markCustom();
}
export function setClouds(on: boolean): void { settings_.cloudsEnabled = on; markCustom(); }
export function setLowClouds(on: boolean): void { settings_.lowClouds = on; markCustom(); }
export function setAAQuality(q: number): void {
  settings_.aaQuality = [2, 4, 8, 16, 32].includes(q) ? q : 2;
  const post = { ...settings_.post };
  if (post.fxaa?.enabled) post.fxaa = { ...post.fxaa, quality: settings_.aaQuality };
  if (post.smaa?.enabled) post.smaa = { ...post.smaa, quality: settings_.aaQuality };
  if (post.traa?.enabled) post.traa = { ...post.traa, quality: settings_.aaQuality };
  settings_.post = post; markCustom();
}
/** Distance fog: metres to the ~63% extinction depth; 0 turns the fog off.
 *  Maps inversely onto fogScale (AtmosphereModule multiplies its tod+weather
 *  density by it; 1200 m ≈ the tier-1.0 baseline). */
export function setFogDistance(m: number): void {
  settings_.fogScale = m <= 0 ? 0 : Math.min(3, Math.max(0.3, 1200 / m));
  markCustom();
}
export function setVolFogDistance(m: number): void {
  const post = { ...settings_.post };
  if (post.volumetricFog) post.volumetricFog = { ...post.volumetricFog, density: 2.5 / Math.max(60, Math.min(600, m)) };
  settings_.post = post; markCustom();
}
export function setBloomStrength(v: number): void {
  const post = { ...settings_.post };
  if (post.bloom) post.bloom = { ...post.bloom, strength: clampNum(v, 0, 2) };
  settings_.post = post; markCustom();
}
export function setMotionBlurIntensity(v: number): void {
  const post = { ...settings_.post };
  if (post.motionBlur) post.motionBlur = { ...post.motionBlur, intensity: clampNum(v, 0.25, 3) };
  settings_.post = post; markCustom();
}
export function setAutoExposureStrength(v: number): void { settings_.autoExposureStrength = clampNum(v, 0, 1); markCustom(); }
export function setDofFocusDistance(v: number): void {
  const post = { ...settings_.post };
  // focusDistance is in world/view metres (CoC = cocScale·(1 − focusDistance/z));
  // allow the focus plane out to 4 km so distant vistas can be brought into focus.
  if (post.dof) post.dof = { ...post.dof, focusDistance: clampNum(v, 1, 4000) };
  settings_.post = post; markCustom();
}
export function setDofFocalLength(v: number): void {
  const post = { ...settings_.post };
  // Metres of focus RANGE (see the high preset). The old 0.005–0.3 range was
  // built around reading this as a lens focal length, so every value on the
  // slider meant "fully blurred within 30 cm of the focus plane".
  if (post.dof) post.dof = { ...post.dof, focalLength: clampNum(v, 4, 400) };
  settings_.post = post; markCustom();
}
export function setDofBokehScale(v: number): void {
  const post = { ...settings_.post };
  if (post.dof) post.dof = { ...post.dof, bokehScale: clampNum(v, 0.3, 4) };
  settings_.post = post; markCustom();
}
export function setCloudParallax(v: number): void { settings_.cloudParallax = clampNum(v, 0, 4); markCustom(); }
export function setModelSmoothing(on: boolean): void { settings_.meshSmoothing = on; markCustom(); }
export function setSurfaceDetail(on: boolean): void { settings_.surfaceDetail = on; markCustom(); }
export function setParticleDensity(v: number): void { settings_.particleCap = clampNum(v, 0, 1); markCustom(); }
/**
 * CUSTOM draw distance (metres, up to 5 km) — the "see far on ANY preset" lever,
 * independent of the graphics tier. Overrides the terrain streaming window
 * (~m/256 chunks) AND the object far-cull; scales settlements up to a moderate
 * cap (they're draw-item heavy); and thins the fog so it never sits NEARER than
 * the draw distance (raising it behind a wall of fog would show nothing).
 */
export const MIN_DRAW_DISTANCE_M = 500; // user 2026-09-08: never nearer than 500 m (objects and the void alike)
export function setDrawDistance(m: number): void {
  const d = clampNum(m, MIN_DRAW_DISTANCE_M, 5000);
  settings_.chunkRadius = Math.max(2, Math.round(d / 256)); // 2 chunks = the 512 m window of the minimal tier
  settings_.farCull = Math.round(d);
  settings_.settlementRadiusScale = clampNum(d / 1000, 1, 3);
  if (settings_.fogScale > 0) settings_.fogScale = Math.min(settings_.fogScale, 1200 / d);
  markCustom();
}
/** Current custom draw distance in metres (the terrain window). */
export function currentDrawDistance(): number { return Math.round((settings_.chunkRadius ?? 3) * 256); }
/** Shadow quality maps to per-cascade map size + cascade count + far distance; 'off' disables CSM entirely. */
export function setShadows(level: ShadowLevel): void {
  if (level === 'off') {
    // Potato-style: no CSM node at all — LightingModule tears the cascades down.
    settings_.shadowsEnabled = false; markCustom(); return;
  }
  const map: Record<Exclude<ShadowLevel, 'off'>, { shadowMapSize: number; shadowCascades: number; shadowMaxFar: number }> = {
    low:   { shadowMapSize: 1024, shadowCascades: 2, shadowMaxFar: 150 },
    med:   { shadowMapSize: 2048, shadowCascades: 3, shadowMaxFar: 200 },
    high:  { shadowMapSize: 2048, shadowCascades: 4, shadowMaxFar: 250 },
    ultra: { shadowMapSize: 4096, shadowCascades: 4, shadowMaxFar: 350 },
  };
  Object.assign(settings_, map[level], { shadowsEnabled: true }); markCustom();
}

// ── Shader-quality setters (2026-08-17 gap-closure wave) ─────────────────────
// Each maps a menu control onto the engine pipeline's new pass configs.

export function setVignette(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.vignette = post.vignette ?? passBack('vignette', { strength: 0.12, radius: 0.25, softness: 0.5 });
  else delete post.vignette;
  settings_.post = post; markCustom();
}
/** 0 disables; otherwise the RCAS-style sharpen strength. */
export function setSharpen(strength: number): void {
  const post = { ...settings_.post };
  if (strength > 0) post.sharpen = { strength: clampNum(strength, 0, 1) };
  else delete post.sharpen;
  settings_.post = post; markCustom();
}
// ── Graphics-menu completion wave: the knobs that existed but had no control ──
export function setFoliageDensity(v: number): void { settings_.foliageDensityScale = clampNum(v, 0, 1.5); markCustom(); }
export function setLodDistanceScale(v: number): void { settings_.lodDistanceScale = clampNum(v, 0.5, 6); markCustom(); }
export function setSettlementDetail(v: number): void { settings_.settlementRadiusScale = clampNum(v, 0.5, 3); markCustom(); }
export function setSettlementLowLod(on: boolean): void { settings_.settlementForceLowLod = on; markCustom(); }
export function setNearFoliageLod(lod: 0 | 1): void { settings_.nearFoliageLod = lod; markCustom(); }
export function setPropLod(lod: 0 | 1 | 2): void { settings_.propLod = lod; markCustom(); }
export function setMaxTextureRes(v: number): void {
  settings_.maxTextureRes = [512, 1024, 2048, 4096].includes(v) ? v : 2048;
  markCustom();
}
export function setAnisotropy(v: number): void {
  settings_.anisotropy = [1, 2, 4, 8, 16].includes(v) ? v : 4;
  markCustom();
}
export function setTonemapExposure(v: number): void { settings_.tonemapExposure = clampNum(v, 0.4, 1.8); markCustom(); }
export function setTonemapOperator(op: 'aces' | 'agx' | 'aces2' | 'neutral' | 'reinhard'): void { settings_.tonemapOperator = op; markCustom(); }
/** 'physical' = the time-of-day exposure curve (the default, 2026-09-25) · 'fixed' = a fixed camera exposure. */
export function setExposureMode(m: 'physical' | 'fixed'): void { settings_.exposureMode = m === 'fixed' ? 'fixed' : 'physical'; markCustom(); }
/** The plane-projected cirrus sheet (applies at the next world entry). */
export function setHighClouds(on: boolean): void { settings_.highClouds = on; markCustom(); }
export function currentHighClouds(): boolean { return settings_.highClouds ?? !!settings_.cloudsEnabled; }
export function currentExposureMode(): 'physical' | 'fixed' { return settings_.exposureMode === 'fixed' ? 'fixed' : 'physical'; }
export function setCloudMarchQuality(q: 'auto' | 'low' | 'medium' | 'high'): void { settings_.cloudMarchQuality = q; markCustom(); }
export function setWaterReflections(on: boolean): void { settings_.waterReflections = on; markCustom(); }
/** Water realism wave (2026-09-25): one setter for the WATER_TIERS switches. */
export function setWaterLook(key: WaterLookKey, on: boolean): void { (settings_ as unknown as Record<string, unknown>)[key] = on; markCustom(); }
export function setColorblindMode(m: 'off' | 'protanopia' | 'deuteranopia' | 'tritanopia'): void { settings_.colorblindMode = m; markCustom(); }
export function setColorblindStrength(v: number): void { settings_.colorblindStrength = clampNum(v, 0, 1); markCustom(); }
export function setCameraFov(deg: number): void { settings_.cameraFov = deg <= 0 ? 0 : clampNum(deg, 50, 100); markCustom(); }
// ── Visual-realism wave setters (2026-09-25, materials / AA / camera / characters) ──
export function setMaterialGrime(on: boolean): void { settings_.materialGrime = on; markCustom(); }
export function setMipBias(on: boolean): void { settings_.mipBias = on; markCustom(); }
export function setPropSurfaceDetail(on: boolean): void { settings_.propSurfaceDetail = on; markCustom(); }
export function setCameraDof(mode: 'off' | 'thirdPerson'): void {
  settings_.cameraDof = mode === 'thirdPerson' ? 'thirdPerson' : 'off';
  // the chain needs a DOF pass for the autofocus to drive; 'off' leaves the pass to the DOF toggle (bokeh 0 in first person anyway)
  const post = settings_.post as Record<string, unknown> & { dof?: Record<string, unknown> };
  if (settings_.cameraDof === 'thirdPerson' && !post.dof) post.dof = { focusDistance: 3, focalLength: 6, bokehScale: 2, aperture: { fNumber: settings_.cameraFNumber ?? 5.6, ...CAMERA_LENS } };
  markCustom();
}
export function setCameraFNumber(n: number): void { settings_.cameraFNumber = clampNum(n, 1.4, 16); markCustom(); }
export function setPlayerBodyRig(on: boolean): void { settings_.playerBodyRig = on; markCustom(); }
export function setFootIk(on: boolean): void { settings_.footIk = on; markCustom(); }
export function setBreathing(on: boolean): void { settings_.breathing = on; markCustom(); }
/** R3: tone-map + AA at render resolution BEFORE EASU (the spatial tiers). */
export function setAaFirst(on: boolean): void {
  const post = settings_.post as Record<string, unknown> & { upscaleOrder?: { aaFirst?: boolean; kernel?: 'amd' | 'legacy' } };
  post.upscaleOrder = { ...(post.upscaleOrder ?? {}), aaFirst: on }; markCustom();
}
/** R3: AMD's 12-tap EASU ('amd') or the first, bilinear-like kernel ('legacy'). */
export function setEasuKernel(k: 'amd' | 'legacy'): void {
  const post = settings_.post as Record<string, unknown> & { upscaleOrder?: { aaFirst?: boolean; kernel?: 'amd' | 'legacy' } };
  post.upscaleOrder = { ...(post.upscaleOrder ?? {}), kernel: k === 'legacy' ? 'legacy' : 'amd' }; markCustom();
}
export function currentAaFirst(): boolean { return !!(settings_.post as { upscaleOrder?: { aaFirst?: boolean } }).upscaleOrder?.aaFirst; }
export function currentEasuKernel(): 'amd' | 'legacy' { return (settings_.post as { upscaleOrder?: { kernel?: 'amd' | 'legacy' } }).upscaleOrder?.kernel ?? 'amd'; }
export function setAutoTune(on: boolean): void {
  // Persist WITHOUT marking custom: the tuner must survive its own setTier
  // steps (custom disables the ladder walk to respect manual tweaks).
  settings_.autoTune = on;
  saveSettings();
}
// Shader-parameter sliders over passes the toggles already own:
export function setBloomThreshold(v: number): void {
  const post = { ...settings_.post };
  if (post.bloom) post.bloom = { ...post.bloom, threshold: clampNum(v, 0.4, 1.6) };
  settings_.post = post; markCustom();
}
export function setBloomRadius(v: number): void {
  const post = { ...settings_.post };
  if (post.bloom) post.bloom = { ...post.bloom, radius: clampNum(v, 0, 1) };
  settings_.post = post; markCustom();
}
export function setAoIntensity(v: number): void {
  const post = { ...settings_.post };
  if (post.ao) post.ao = { ...post.ao, intensity: clampNum(v, 0, 3) };
  settings_.post = post; markCustom();
}
export function setAoRadius(v: number): void {
  const post = { ...settings_.post };
  if (post.ao) post.ao = { ...post.ao, radius: clampNum(v, 0.1, 2) };
  settings_.post = post; markCustom();
}
export function setVignetteStrength(v: number): void {
  const post = { ...settings_.post };
  if (post.vignette) post.vignette = { ...post.vignette, strength: clampNum(v, 0, 1) };
  settings_.post = post; markCustom();
}
export function setFilmGrainIntensity(v: number): void {
  const post = { ...settings_.post };
  if (post.film) post.film = { ...post.film, intensity: clampNum(v, 0, 1) };
  settings_.post = post; markCustom();
}
export function setChromaticStrength(v: number): void {
  const post = { ...settings_.post };
  if (post.chromaticAberration) post.chromaticAberration = { ...post.chromaticAberration, strength: clampNum(v, 0, 2) };
  settings_.post = post; markCustom();
}
export function setMotionBlurSamples(v: number): void {
  const post = { ...settings_.post };
  if (post.motionBlur) post.motionBlur = { ...post.motionBlur, samples: Math.round(clampNum(v, 4, 32)) };
  settings_.post = post; markCustom();
}
// Engine passes never wired before (config-key presence = pass included):
export function setSsr(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.ssr = post.ssr ?? passBack('ssr', { intensity: 1.0, maxDistance: 40, thickness: 0.35, resolutionScale: 0.5 });
  else delete post.ssr;
  settings_.post = post; markCustom();
}
export function setContactShadows(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.contactShadows = post.contactShadows ?? passBack('contactShadows', { enabled: true });
  else delete post.contactShadows;
  settings_.post = post; markCustom();
}
export function setSsgi(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.ssgi = post.ssgi ?? passBack('ssgi', { enabled: true });
  else delete post.ssgi;
  settings_.post = post; markCustom();
}
export function setLensFlares(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.lensFlares = post.lensFlares ?? passBack('lensFlares', { enabled: true });
  else delete post.lensFlares;
  settings_.post = post; markCustom();
}
/** The colour look — a procedural 3D LUT for the engine's lut3D pass (PostModule bakes it). */
export function setLook(look: TierPreset['look']): void { settings_.look = look; markCustom(); }
export function setFoliageShading(on: boolean): void { settings_.foliageShading = on; markCustom(); }
export function setFoliageWind(on: boolean): void { settings_.foliageWind = on; markCustom(); }
/** Whether foliage wind should run: its own switch, defaulting to the shading flag. */
export function foliageWindOn(preset = settings_): boolean {
  return preset.foliageWind ?? (preset.foliageShading !== false);
}
export function currentFoliageWind(): boolean { return foliageWindOn(); }
export function setFluidPhysics(on: boolean): void { settings_.fluidPhysics = on; markCustom(); }
export function setMaterialFx(on: boolean): void { settings_.materialFx = on; markCustom(); }
// Shader wave stage 3 (2026-09-09): the remaining engine features.
export function setInteriorMapping(on: boolean): void { settings_.interiorMapping = on; markCustom(); }
export function setVfxShaders(on: boolean): void { settings_.vfxShaders = on; markCustom(); }
export function setLitDecals(on: boolean): void { settings_.litDecals = on; markCustom(); }
export function setLampCookies(on: boolean): void { settings_.lampCookies = on; markCustom(); }
export function setWaterFoam(on: boolean): void { settings_.waterFoam = on; markCustom(); }
export function setWaterCaustics(on: boolean): void { settings_.waterCaustics = on; markCustom(); }
// Shader wave stage 4 (2026-09-09): the new engine shaders.
export function setCloudShadows(on: boolean): void { settings_.cloudShadows = on; markCustom(); }
export function setGroundAntiTiling(on: boolean): void { settings_.groundAntiTiling = on; markCustom(); }
export function setRainRipples(on: boolean): void { settings_.rainRipples = on; markCustom(); }
export function setAurora(on: boolean): void { settings_.aurora = on; markCustom(); }
export function setNebulae(on: boolean): void { settings_.nebulae = on; markCustom(); }
export function setPlanetAtmosphere(on: boolean): void { settings_.planetAtmosphere = on; markCustom(); }
// ── The space realism wave (2026-09-25, the space agent) ──
export function setSpaceSky(on: boolean): void { settings_.spaceSky = on; markCustom(); }
export function setSpaceMotion(on: boolean): void { settings_.spaceMotion = on; markCustom(); }
export function setSpacePlumes(on: boolean): void { settings_.spacePlumes = on; markCustom(); }
export function setSpaceLitHulls(on: boolean): void { settings_.spaceLitHulls = on; markCustom(); }
export function setSpaceShields(on: boolean): void { settings_.spaceShields = on; markCustom(); }
export function setEclipseShadows(on: boolean): void { settings_.eclipseShadows = on; markCustom(); } // shader gaps R4
export function setPlanetRings(on: boolean): void { settings_.planetRings = on; markCustom(); } // shader gaps R4
export function setOrbitalClouds(on: boolean): void { settings_.orbitalClouds = on; markCustom(); } // shader gaps R4
export function setUnderwaterShafts(on: boolean): void { settings_.underwaterShafts = on; markCustom(); }
export function setDqSkinning(on: boolean): void { settings_.dqSkinning = on; markCustom(); }
// Shader additions wave (2026-09-15).
export function setSpecularAA(on: boolean): void { settings_.specularAA = on; markCustom(); }
export function setProceduralFiltering(on: boolean): void { settings_.proceduralFiltering = on; markCustom(); }
export function setVolumetricLights(on: boolean): void { settings_.volumetricLights = on; markCustom(); }
export function setLitSmoke(on: boolean): void { settings_.litSmoke = on; markCustom(); }
export function setAreaLights(on: boolean): void { settings_.areaLights = on; markCustom(); }
export function setGlassTransmission(on: boolean): void { settings_.glassTransmission = on; markCustom(); }
export function setHardSurfaceAnisotropy(on: boolean): void { settings_.hardSurfaceAnisotropy = on; markCustom(); }
export function setRetroreflection(on: boolean): void { settings_.retroreflection = on; markCustom(); }
// Render wave 1 (2026-09-17).
export function setImpostorBlend(on: boolean): void { settings_.impostorBlend = on; markCustom(); }
export function setRainShelter(on: boolean): void { settings_.rainShelter = on; markCustom(); }
// Vegetation realism (2026-09-25, visual-realism R2–R12).
const GRASS_LAYERS = ['off', 'cards', 'low', 'med', 'high', 'ultra'] as const;
export function setGrassLayer(t: TierPreset['grassLayer']): void { settings_.grassLayer = (GRASS_LAYERS as readonly string[]).includes(t) ? t : 'off'; markCustom(); }
export function setGeologyDensity(v: number): void { settings_.geologyDensity = clampNum(v, 0, 1.5); markCustom(); }
export function setUnderstoryDensity(v: number): void { settings_.understoryDensity = clampNum(v, 0, 1.5); markCustom(); }
export function setForestHorizon(m: number): void { settings_.forestHorizonM = clampNum(Math.round(m), 0, 12000); markCustom(); }
export function setFarCanopy(on: boolean): void { settings_.farCanopy = on; markCustom(); }
export function setFoliageJitter(on: boolean): void { settings_.foliageJitter = on; markCustom(); }
export function setAmbientLife(on: boolean): void { settings_.ambientLife = on; markCustom(); }
export function setCaveLight(m: 'off' | 'visibility' | 'baked' | 'baked+probe'): void { settings_.caveLight = m === 'off' || m === 'visibility' || m === 'baked' ? m : 'baked+probe'; markCustom(); }
export function setMaterialDamage(m: 'off' | 'marks' | 'full'): void { settings_.materialDamage = m === 'off' || m === 'marks' ? m : 'full'; markCustom(); }
export function setEonDiffuse(on: boolean): void { settings_.eonDiffuse = on; markCustom(); } // shader gaps R4: material models
export function setLaplacianBlend(on: boolean): void { settings_.laplacianBlend = on; markCustom(); } // shader gaps R4
export function setFlakeGlints(on: boolean): void { settings_.flakeGlints = on; markCustom(); } // shader gaps R4
export function setWindowShadows(on: boolean): void { settings_.windowShadows = on; markCustom(); } // shader gaps R5: window-light occluders
export function setSkinShading(on: boolean): void { settings_.skinShading = on; markCustom(); }
export function setWetSurfaces(m: 'off' | 'ground' | 'all'): void { settings_.wetSurfaces = m === 'off' || m === 'ground' ? m : 'all'; markCustom(); } // shader gaps R3
export function setSurfaceDeform(on: boolean): void { settings_.surfaceDeform = on; markCustom(); } // shader gaps R3
export function setFftOcean(on: boolean): void { settings_.fftOcean = on; markCustom(); } // shader gaps R5: the FFT ocean
export function setBlackHole(q: 'off' | 'low' | 'high'): void { settings_.blackHole = q === 'off' || q === 'low' ? q : 'high'; markCustom(); } // shader gaps: black hole
export function setInteriorDetail(d: 0 | 1 | 2): void { settings_.interiorDetail = d === 0 || d === 2 ? d : 1; markCustom(); } // interiors programme, slice D
export function setPuddleSim(s: 'off' | 'small' | 'medium' | 'large'): void { settings_.puddleSim = s === 'small' || s === 'medium' || s === 'large' ? s : 'off'; markCustom(); } // shader gaps R6: the puddle patch
export function setVolumeResolution(r: 'full' | 'half'): void { settings_.volumeResolution = r === 'full' ? 'full' : 'half'; markCustom(); } // shader gaps R3 (agent F)
export function setVolumeNoise(n: 'golden' | 'stbn' | 'white'): void { settings_.volumeNoise = n === 'stbn' || n === 'white' ? n : 'golden'; markCustom(); } // shader gaps R3 (agent F)
// Fire realism (2026-09-25).
export function setFireQuality(q: 'sprite' | 'cards' | 'volume' | 'sim'): void { settings_.fireQuality = q === 'sprite' || q === 'cards' || q === 'sim' ? q : 'volume'; markCustom(); }
export function setFireLights(n: number): void { settings_.fireLights = clampNum(Math.round(n), 1, 3); markCustom(); }
export function setFireHaze(on: boolean): void { settings_.fireHaze = on; markCustom(); }
export function setAutoExposureMetering(m: 'average' | 'histogram'): void { settings_.autoExposureMetering = m; markCustom(); }
export function currentAutoExposureMetering(): 'average' | 'histogram' { return settings_.autoExposureMetering ?? 'average'; }
/** The bloom filter: three's Gaussian mip chain or the dual-Kawase blur (only meaningful while bloom is on). */
/**
 * 'kawase' = the energy-conserving veil (mix(scene, blur, ~0.04), threshold-less — 2026-09-25);
 * 'mip' = three's Gaussian mip chain, the legacy ADDITIVE glow over a 1.1 threshold.
 */
export function setBloomMode(mode: 'mip' | 'kawase'): void {
  const post = { ...settings_.post };
  if (post.bloom) {
    post.bloom = mode === 'kawase'
      ? { ...post.bloom, mode, composite: 'lerp', strength: post.bloom.composite === 'lerp' ? post.bloom.strength : 0.04, passes: 5 }
      : { ...post.bloom, mode, composite: 'add', threshold: post.bloom.threshold ?? 1.1, strength: post.bloom.composite === 'lerp' ? 0.45 : post.bloom.strength };
  }
  settings_.post = post; markCustom();
}
export function currentBloomMode(): 'mip' | 'kawase' { return (settings_.post.bloom?.mode as 'mip' | 'kawase' | undefined) ?? 'mip'; }
export function setGodRays(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.godRays = post.godRays ?? passBack('godRays', { enabled: true, numSamples: 48, decay: 0.965, exposure: 0.22, density: 1.0, weight: 0.01 });
  else delete post.godRays;
  settings_.post = post; markCustom();
}

/** 1 = neutral (removes the grade pass); else a saturation-only colour grade. */
export function setSaturation(v: number): void {
  const post = { ...settings_.post };
  const sat = clampNum(v, 0.5, 1.5);
  if (Math.abs(sat - 1) < 0.01) delete post.colorGrade;
  else post.colorGrade = { saturation: sat };
  settings_.post = post; markCustom();
}
export function setVolumetricFogPass(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.volumetricFog = post.volumetricFog ?? passBack('volumetricFog', { density: 0.012, heightFalloff: 0.05, sunPower: 8 });
  else delete post.volumetricFog;
  settings_.post = post; markCustom();
}
export function setMotionBlur(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.motionBlur = post.motionBlur ?? passBack('motionBlur', { samples: 12 });
  else delete post.motionBlur;
  settings_.post = post; markCustom();
}
export function setFilmGrain(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.film = post.film ?? passBack('film', { intensity: 0.015, mode: 'luma' });
  else delete post.film;
  settings_.post = post; markCustom();
}
export function setChromaticAberration(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.chromaticAberration = post.chromaticAberration ?? passBack('chromaticAberration', { strength: 0.6, scale: 1.04 });
  else delete post.chromaticAberration;
  settings_.post = post; markCustom();
}
export function setCapsuleShadows(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.capsuleShadows = post.capsuleShadows ?? passBack('capsuleShadows', { intensity: 0.55, penumbra: 0.3, maxDistance: 5 });
  else delete post.capsuleShadows;
  settings_.post = post; markCustom();
}
/** Applies at next reload — the sky bakes the froxel volume only when created with it on. */
export function setAerialPerspective(on: boolean): void { settings_.aerialEnabled = on; markCustom(); }
export function setAutoExposure(on: boolean): void { settings_.autoExposure = on; markCustom(); }
/** Bokeh style for the DoF pass (only meaningful while DoF is on). */
export function setBokehStyle(style: 'standard' | 'hex'): void {
  const post = { ...settings_.post };
  if (post.dof) post.dof = { ...post.dof, bokeh: style };
  settings_.post = post; markCustom();
}

export function currentVignette(): boolean { return !!settings_.post.vignette; }
export function currentSharpen(): number { return settings_.post.sharpen?.strength ?? 0; }
export function currentSaturation(): number { return settings_.post.colorGrade?.saturation ?? 1; }
export function currentVolumetricFogPass(): boolean { return !!settings_.post.volumetricFog; }
export function currentMotionBlur(): boolean { return !!settings_.post.motionBlur; }
export function currentFilmGrain(): boolean { return !!settings_.post.film; }
export function currentChromaticAberration(): boolean { return !!settings_.post.chromaticAberration; }
export function currentCapsuleShadows(): boolean { return !!settings_.post.capsuleShadows; }
export function currentAerialPerspective(): boolean { return settings_.aerialEnabled; }
export function currentAutoExposure(): boolean { return settings_.autoExposure; }
export function currentLowClouds(): boolean { return settings_.lowClouds; }
export function currentAAQuality(): number { return settings_.aaQuality; }
export function currentFogDistance(): number { return settings_.fogScale <= 0 ? 0 : Math.round(1200 / settings_.fogScale); }
export function currentVolFogDistance(): number { const d = settings_.post.volumetricFog?.density; return d ? Math.round(2.5 / d) : 0; }
export function currentBloomStrength(): number { return settings_.post.bloom?.strength ?? 0; }
export function currentMotionBlurIntensity(): number { return settings_.post.motionBlur?.intensity ?? 1; }
export function currentAutoExposureStrength(): number { return settings_.autoExposureStrength; }
export function currentDofFocusDistance(): number { return settings_.post.dof?.focusDistance ?? 18; }
export function currentDofFocalLength(): number { return settings_.post.dof?.focalLength ?? 55; }
export function currentDofBokehScale(): number { return settings_.post.dof?.bokehScale ?? 1.2; }
export function currentCloudParallax(): number { return settings_.cloudParallax; }
export function currentBokehStyle(): 'standard' | 'hex' { return settings_.post.dof?.bokeh ?? 'standard'; }

/** Current per-feature values, for the menu to render its controls from. */
export function currentAA(): AAMode {
  if (settings_.post.traa?.enabled) return 'traa';
  if (settings_.post.smaa?.enabled) return 'smaa';
  if (settings_.post.fxaa && settings_.post.fxaa.enabled === false) return 'off';
  // No explicit off ⇒ the engine's default FXAA runs (even with no fxaa key).
  return 'fxaa';
}
export function currentShadows(): ShadowLevel {
  if (!settings_.shadowsEnabled) return 'off';
  const s = settings_.shadowMapSize, c = settings_.shadowCascades;
  if (s >= 4096) return 'ultra';
  if (s >= 2048 && c >= 4) return 'high';
  if (s >= 2048) return 'med';
  return 'low';
}
export function currentModelSmoothing(): boolean { return settings_.meshSmoothing; }
export function currentSurfaceDetail(): boolean { return settings_.surfaceDetail; }

// ── Persistence ───────────────────────────────────────────────────────────────
const LS_KEY = 'atlas:graphics';

/** Persist the active tier + the live settings (so custom tweaks survive a reload). */
// xr (2026-09-28): while a VR session overlays the live preset, what persists is the desktop snapshot — a save from
// anywhere during the session (a setter, a probe lever) must never write the headset's values over the desktop's.
let persistOverride_: TierPreset | null = null;
export function setPersistOverride(p: TierPreset | null): void { persistOverride_ = p; }

export function saveSettings(): void {
  if (inDraft_) return; // a draft is not the player's settings until "Apply changes"
  liveStamp_++;         // every live mutation persists through here: an open draft re-bases on it
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(LS_KEY, JSON.stringify({ baseTier: baseTier_, custom: custom_, settings: persistOverride_ ?? settings_ }));
  } catch { /* storage blocked — settings just won't persist */ }
}

/**
 * True when this browser has locally-saved graphics settings. The server's
 * device_tier_hint must only seed a tier for a FIRST-ever login — applying it
 * over saved settings stomped the player's persisted/custom choices every
 * session (and reverted any tier outside the hint's low|med|high vocabulary).
 */
export function hasSavedSettings(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(LS_KEY) !== null;
  } catch { return false; }
}

/** Restore persisted settings, or auto-detect a starting preset on first run. Call once at boot. */
export function loadSettings(): void {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null;
    if (raw) {
      const parsed = JSON.parse(raw) as { baseTier?: Tier; custom?: boolean; settings?: Partial<TierPreset> };
      if (parsed.baseTier && TIERS[parsed.baseTier]) {
        baseTier_ = parsed.baseTier;
        custom_ = !!parsed.custom;
        // Start from a clean base preset, then overlay the persisted (possibly customised) fields —
        // so a TierPreset schema change adds new fields with sane preset defaults instead of `undefined`.
        // A CUSTOMISED profile is the player's, post included: a pass they switched off is ABSENT from
        // `post`, so merging the preset's `post` underneath switched it back on at every reload (and an AA of
        // Off / FXAA reverted to the preset's SMAA — `currentAA` reads smaa first), and the next save made it
        // permanent (bug sweep 2026-09-23). An UNCUSTOMISED profile IS the preset — today's preset, so a
        // retune (the halved draw distance) reaches it — plus the sticky preferences a preset does not carry.
        const saved = (parsed.settings ?? {}) as Partial<TierPreset>;
        settings_ = custom_
          ? { ...clonePreset(TIERS[baseTier_]), ...saved, post: saved.post ? { ...saved.post } : { ...TIERS[baseTier_].post } }
          : withSticky(clonePreset(TIERS[baseTier_]), saved as unknown as Record<string, unknown>);
        // One-shot migrations (2026-08-18/19): 2.5, 0 and 0.15 were each the
        // shipped cloudParallax default for a few hours/days (exaggerated-
        // parallax, then fully-frozen, then travel-evolution) before the
        // world-anchored default (1.0) — a persisted 2.5/0/0.15 was almost
        // certainly never a deliberate choice; let the preset default flow.
        // Any other stored value is a real choice and is kept.
        if (parsed.settings?.cloudParallax === 2.5 || parsed.settings?.cloudParallax === 0 || parsed.settings?.cloudParallax === 0.15) {
          settings_.cloudParallax = TIERS[baseTier_].cloudParallax;
        }
        return;
      }
    }
  } catch { /* corrupt/blocked — fall through to auto-detect */ }
  // First run (or unreadable): auto-detect from the device — unless a browser under AUTOMATION, which
  // boots on the minimal tier (THE PROBE TIER LAW: a fresh headless profile once auto-detected ULTRA and
  // swiftshader died carrying its post chain; a probe that wants a tier seeds 'atlas:graphics').
  setActiveTier(defaultTierFor(underAutomation(), detectDefaultTier()));
}

/** navigator.webdriver — puppeteer / playwright / any WebDriver session. */
export function underAutomation(): boolean {
  return typeof navigator !== 'undefined' && (navigator as { webdriver?: boolean }).webdriver === true;
}
/** The boot tier with nothing saved: the detected one for a person, 'minimal' for a robot. */
export function defaultTierFor(automation: boolean, detected: Tier): Tier {
  return automation ? 'minimal' : detected;
}

/**
 * Pick a sensible starting preset from device capability so a weak machine isn't
 * dropped into Ultra. Heuristic only — the player can override in the menu.
 */
export function detectDefaultTier(): Tier {
  let cores = 4, mem = 4, mobile = false;
  try {
    if (typeof navigator !== 'undefined') {
      cores = (navigator.hardwareConcurrency as number) || 4;
      mem = ((navigator as unknown as { deviceMemory?: number }).deviceMemory) || 4; // GB, Chromium-only
    }
    mobile = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
  } catch { /* defaults */ }
  // xr (2026-09-28): a standalone headset's browser (Quest / Pico / Galaxy XR) runs a mobile XR2 GPU behind a desktop
  // UA that reports 6–8 cores, so the core count put it on 'high'. Its 2D page starts on 'low'; VR overlays its own
  // levels on top (lib/xr_profile.ts) and never touches this.
  let headset = false;
  try { headset = typeof navigator !== 'undefined' && /OculusBrowser|\bQuest\b|Pico|SM-I610/i.test(navigator.userAgent ?? ''); } catch { /* defaults */ }
  if (mobile || headset || cores <= 2 || mem <= 2) return 'low';
  if (cores <= 4 || mem <= 4) return 'med';
  if (cores >= 12 && mem >= 8) return 'ultra';
  return 'high';
}

function clampNum(v: number, lo: number, hi: number): number { return v < lo ? lo : v > hi ? hi : v; }
